import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  BUILT_FORM_OPTIONS,
  can,
  PROPERTY_TYPE_OPTIONS,
  SURVEY_PROPERTY_FIELDS,
  WALL_CONSTRUCTION_OPTIONS,
  WALL_INSULATION_OPTIONS,
  type BuiltForm,
  type SurveyPropertyFacts,
  type VocabularyOption,
} from "@sts/schemas";
import { formatArea, formatLength } from "@sts/scan-viewer";
import {
  applyCalibration,
  applyPlanEdits,
  effectiveRoomGroups,
  calibrationMarker,
  formatCalibrationFactor,
  planOrientation,
  scanPlanRooms,
  DRAWN_CEILING_RANGE_M,
  DRAWN_MIN_SIDE_M,
  drawnPlanCapture,
  scanRoomGroups,
  type CalibratedCapture,
  type PlanEditStatus,
  type RoomCalibration,
  type RoomPlanCapture,
  type ScanElement,
  type ScanRoomGroup,
} from "@sts/geometry";
import type {
  Assumption,
  Me,
  ProvenancedValue,
  ScanCalibrationList,
  SpaceModel,
  SurveyHeatLoss,
  SurveyPropertyDetails,
  SurveyPropertyInput,
  SurveyRoom,
  SurveyScan,
  SurveySummary,
  SurveySurface,
} from "../api/types.js";
import type { PhotoEvidence } from "../api/capture-types.js";
import { ScanBreakdown } from "../scan/ScanBreakdown.jsx";
import { FloorPlan } from "../scan/FloorPlan.jsx";
import { PlanExportMenu } from "../scan/PlanExportMenu.jsx";
import { DesignReadinessCard } from "../components/DesignReadinessCard.jsx";
import { SiteSurveyCard } from "../components/SiteSurveyCard.jsx";
import { DesignConditionsCard } from "../components/DesignConditionsCard.jsx";
import { DefaultMaterialsCard } from "../components/DefaultMaterialsCard.jsx";
import { RoomNotes } from "../components/RoomNotes.jsx";
import { RoomFloorCeiling } from "../components/RoomFloorCeiling.jsx";
import { planImagesFromApi } from "../scan/plan-image.jsx";
import { RoomHeatLossBreakdown, WholeHouseHeatLossByElement } from "../components/RoomHeatLossBreakdown.jsx";
import { CalibrationPanel } from "../scan/CalibrationPanel.jsx";
import { appliedCalibrations } from "../scan/calibration.js";
import { planObjectsFromApi } from "../scan/use-plan-objects.js";
import { designCheckFromApi } from "../scan/use-design-check.js";
import {
  heatLossPlanNotice,
  planEditsFromApi,
  usePlanEdits,
  type PlanEditLayer,
  type PlanEditsState,
} from "../scan/use-plan-edits.js";
import { planDimensionsFromApi } from "../scan/use-plan-dimensions.js";
import {
  drawnPlanFromApi,
  roomFromCorners,
  useDrawnPlan,
  type DrawnPlanState,
} from "../scan/use-drawn-plan.js";
import { DrawPlanPanel, readMm } from "../scan/DrawPlanPanel.jsx";
import { planCommentsFromApi, type PlanCommentViewer } from "../scan/use-plan-comments.js";
import { ScanViewerWithComments } from "../scan/ScanViewerWithComments.jsx";
import { PhotoRail } from "../components/PhotoRail.jsx";
import { SurveySharePanel } from "../components/SurveySharePanel.jsx";
import { WallAdjacencyCheck } from "../components/WallAdjacencyCheck.jsx";
import { RoomPanel } from "../components/RoomPanel.jsx";
import { SurfacePanel, wallLengthM } from "../components/SurfacePanel.jsx";
import { ValidationList } from "../components/ValidationList.jsx";
import {
  heatLossTrustSentence,
  validateSurvey,
  withUnsnappedObjects,
  unsnappedObjects,
  type UnsnappedObject,
  type ValidationProblem,
  type ValidationReport,
} from "../lib/survey-validation.js";
import {
  assumedOutsideWalls,
  builtFormSuggestion,
  heatLossRoomFor,
  heatLossWallFinder,
  type HeatLossWall,
} from "../lib/wall-adjacency.js";
import { SpaceWorkspace, formatDuration } from "../scan/SpaceWorkspace.jsx";
import { formatMegabytes, spaceStageLabel, useSpaceModel } from "../scan/use-space-model.js";
import { Async, Failure } from "../components/Async.jsx";
import { ProvenanceChip, ProvenanceTally, ProvenancedNumber } from "../components/Chips.jsx";
import { usePortal, useSession } from "../app/context.js";
import { useAsync, type AsyncState } from "../app/use-async.js";
import { useSubmit } from "../app/use-submit.js";
import { href } from "../app/router.js";
import {
  airChanges,
  cubicMetres,
  degrees,
  humanise,
  longDate,
  squareMetres,
  utcMoment,
  uValue as formatU,
  watts,
  wattsPerM2,
} from "../lib/format.js";

/** How an element in the room tables came to differ from the scan. */
type EditMark = PlanEditStatus | "followed";

/**
 * The survey, and how much of it is guesswork.
 *
 * Every other tool in this market prints one undifferentiated table of numbers.
 * The argument this product makes is that the assumed values dominate the error
 * — the BRE's in-situ work put solid walls nearer 1.6 W/m²K than the 2.1 an
 * age band assumes, which swamps anything a scan contributes — so the screen is
 * built to make the difference impossible to miss rather than available on
 * request.
 */
interface SurveyPage {
  readonly survey: SurveySummary;
  readonly me: Me;
}

export function Survey({
  id,
  show = "all",
}: {
  readonly id: string;
  /** "plan" renders the drawing alone: see the note in `Detail`. */
  readonly show?: "all" | "plan";
}): ReactNode {
  const { client, known } = usePortal();
  const session = useSession();
  const organisationId = session?.organisationId ?? null;

  const { state, reload } = useAsync<SurveyPage>(async () => {
    const [survey, me] = await Promise.all([client.getSurvey(id), client.me()]);
    return { survey, me };
  }, [client, id]);

  const jobId = state.status === "ready" ? state.data.survey.jobId : null;
  useEffect(() => {
    if (jobId !== null) {
      known.remember("survey", organisationId, id, { jobId });
      known.remember("job", organisationId, jobId);
    }
  }, [known, organisationId, id, jobId]);

  return (
    <Async state={state} what="this survey" reload={reload}>
      {(page) => (
        <Detail survey={page.survey} me={page.me} reload={reload} show={show} />
      )}
    </Async>
  );
}

/** How long the plan must be still, after its last save, before the figures are re-read. */
const FIGURES_SETTLE_MS = 300;

function Detail({
  survey,
  me,
  reload,
  show,
}: {
  readonly survey: SurveySummary;
  readonly me: Me;
  readonly reload: () => void;
  readonly show: "all" | "plan";
}): ReactNode {
  const { client } = usePortal();
  // The heat-loss figures, re-read after a laser calibration re-converted the
  // rooms on the server — without reloading the page, which would remount the
  // 3D workspace and lose the calibration panel's answer. `from` ties a
  // re-read to the load it refreshed, so a full reload supersedes it.
  const [refreshed, setRefreshed] = useState<{
    readonly from: SurveySummary;
    readonly survey: SurveySummary;
  } | null>(null);
  const [figuresRevision, setFiguresRevision] = useState(0);
  const refreshGeneration = useRef(0);
  const current = refreshed !== null && refreshed.from === survey ? refreshed.survey : survey;
  const refreshFigures = useCallback(() => {
    const run = ++refreshGeneration.current;
    setFiguresRevision((n) => n + 1);
    client.getSurvey(survey.id).then(
      (next) => {
        if (refreshGeneration.current === run) setRefreshed({ from: survey, survey: next });
      },
      // The figures on screen stay; the next page load reads the new ones.
      () => {},
    );
  }, [client, survey]);

  // The plan's corrections (migration 0039): held here, not in the plan,
  // because the heat loss below must say when the plan has moved on from it.
  const planEditsSource = useMemo(() => planEditsFromApi(client, survey.id), [client, survey.id]);
  const planEdits = usePlanEdits(planEditsSource, survey.id, { meId: me.userId });
  // A saved correction that moved a wall re-converts the heat-loss rooms on
  // the server (as a calibration does): re-read the figures — once the queue
  // of saves has drained and the hand has been still for a moment, so a run
  // of drags, undos and redos is one re-read, not one per change.
  const seenRecalculations = useRef(planEdits.recalculations);
  useEffect(() => {
    if (planEdits.saving || planEdits.recalculations === seenRecalculations.current) return;
    const timer = setTimeout(() => {
      seenRecalculations.current = planEdits.recalculations;
      refreshFigures();
    }, FIGURES_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [planEdits.saving, planEdits.recalculations, refreshFigures]);
  // The same matrix the API enforces: `survey: update`, own scope via assignment.
  const canEditPlan = can({ role: me.role, resource: "survey", action: "update", isAssigned: true });

  const values = collectValues(current.rooms);
  /**
   * What is still wrong with this survey. Computed ONCE, here, from the rooms
   * the page is showing, and handed to both the list beside the plan and the
   * heat-loss section — so the count on the drawing and the sentence under the
   * figure can never disagree about how much is left to answer.
   */
  const validation = useMemo(() => validateSurvey(current.rooms), [current.rooms]);
  /** A room-panel save changes the survey AND the heat loss; re-read both. */
  const reloadAndRefresh = refreshFigures;
  // Walls the scan called outside only because nothing was scanned behind
  // them — maybe party walls (0045). Answered through the survey's own edit
  // path, so only for someone who may change it, on a survey still taking
  // changes: draft or complete (the phone completes every survey as the
  // surveyor leaves), never superseded.
  const outsideWallsToCheck = assumedOutsideWalls(current.rooms);
  const canAnswerWalls = canEditPlan && current.status !== "superseded";
  // Sharing a household's survey outside the organisation is `job: share` —
  // owner and admin — exactly as the API enforces it, so the button is only
  // offered to someone the API will say yes to.
  const canShare = can({ role: me.role, resource: "job", action: "share" });
  const [sharing, setSharing] = useState(false);
  // The stock report and the draft installer pack are the office's and the
  // designers' working screen — organisation-wide `design: read`, as the
  // stock catalogue itself is (design/stock.ts). A field role booked onto the
  // job (surveyor, installer) is pointed at the job's documents instead, where
  // the issued pack is, marked current or superseded.
  const canOpenStock = can({ role: me.role, resource: "design", action: "read" });
  // The (still disabled) design-tools placeholder is for someone who may
  // change the survey or the design — not for a role that can only read both.
  const canEditDesign =
    canEditPlan || can({ role: me.role, resource: "design", action: "update" });

  // Delete the whole survey — owner/admin only (`survey: delete`), the same
  // matrix the API enforces, so the button is only offered to someone the API
  // will say yes to. Two-step: a first click arms it, a second confirms.
  const canDelete = can({ role: me.role, resource: "survey", action: "delete" });
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deletion = useSubmit();
  // Export the whole survey as one machine-readable JSON document — the same
  // model the PDF and the room schedule are drawn from — saved straight to the
  // browser, exactly as the space-model USDZ download does.
  const exportModel = useSubmit();
  const photosOnly = useSubmit();
  const save = (file: { url: string; filename: string }): void => {
    const a = document.createElement("a");
    a.href = file.url;
    a.download = file.filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Long enough for the browser to take the bytes; then let the blob go.
    setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
  };
  const exportJson = (): void =>
    void exportModel.submit(async () => save(await client.surveyModelJson(survey.id)));
  // The hand-off bundle for another portal: the agreed document plus the scan
  // and photographs it references. A survey with no address is refused by the
  // API with the reason, which `Failure` shows — better than a file the far end
  // rejects after the transfer.
  const handOff = useSubmit();
  const exportForPortal = (): void =>
    void handOff.submit(async () => save(await client.surveyIngestExport(survey.id, "zip")));

  // The pictures on their own, for the errand that is not a hand-over: sending
  // them to a customer, an assessor or a colleague, where a survey.json nobody
  // asked for is one more file to explain.
  const exportPhotos = (): void =>
    void photosOnly.submit(async () => save(await client.surveyPhotosExport(survey.id)));

  /*
   * THE DRAWING, AND ONLY THE DRAWING.
   *
   * `#/surveys/<id>/plan` is one of the four doors off a survey's overview,
   * and a door labelled "The drawing" that opens onto the readiness checks,
   * the design conditions, the materials, the heat loss, the assumptions, the
   * 3D model, the photographs and a Mark complete banner is not a door, it is
   * the old screen with a new name.
   *
   * Everything left out is still reachable: the overview's other three doors,
   * and the sections themselves on this screen's full form, which the job
   * still links to. Nothing was deleted to make this.
   */
  if (show === "plan") {
    return (
      <>
        <div className="page-header">
          <p className="small">
            <a href={href.survey(survey.id)}>Back to the survey</a>
          </p>
          <h1>The drawing</h1>
          <p className="lede">
            {current.counts.rooms} rooms, {current.counts.surfaces} surfaces. Exports are on the
            survey&rsquo;s own page.
          </p>
        </div>

        <SurveyWorkspace
          surveyId={survey.id}
          jobId={survey.jobId}
          me={me}
          planEdits={planEdits}
          canEditPlan={canEditPlan}
          figuresRevision={figuresRevision}
          onFiguresChanged={refreshFigures}
          heatLossRooms={current.rooms}
          canAnswerWalls={canAnswerWalls}
          validation={validation}
          onRoomChanged={reloadAndRefresh}
          withPhotos={false}
        />
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.job(survey.jobId)}>Back to the job</a>
          {" · "}
          {canOpenStock ? (
            <a href={href.surveyStock(survey.id)}>Stock &amp; installer pack</a>
          ) : (
            <a href={href.jobDocuments(survey.jobId)}>Installer pack and documents</a>
          )}
        </p>
        <div className="survey-header-row">
          <div>
            <h1>Survey</h1>
            <p className="lede">
              Surveyed {longDate(survey.surveyedOn)} · {humanise(survey.status)} ·{" "}
              {current.counts.rooms} rooms, {current.counts.surfaces} surfaces
            </p>
          </div>
          <div className="survey-header-row__tools">
            <button
              type="button"
              className="secondary"
              disabled={exportModel.busy}
              onClick={exportJson}
            >
              {exportModel.busy ? "Preparing…" : "Export JSON"}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={handOff.busy}
              onClick={exportForPortal}
              title="The agreed hand-off document plus the scan and photographs, as a ZIP"
            >
              {handOff.busy ? "Bundling…" : "Export for portal"}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={photosOnly.busy}
              onClick={exportPhotos}
              title="Just the photographs, as a ZIP. Each one carries its position and capture time stamped into the image"
            >
              {photosOnly.busy ? "Bundling…" : "Export images only"}
            </button>
            {/* Re-read this survey from the server — for when the phone has just
                synced new captures and the portal is still showing the old ones. */}
            <button type="button" className="secondary" onClick={reload}>
              Resync
            </button>
            {canShare ? (
              <button
                type="button"
                className="secondary"
                aria-expanded={sharing}
                aria-controls="survey-share-panel"
                onClick={() => setSharing((open) => !open)}
              >
                Share
              </button>
            ) : null}
            {canEditDesign ? <DesignToolbar /> : null}
            {canDelete ? (
              confirmingDelete ? (
                <>
                  <button
                    type="button"
                    className="danger"
                    disabled={deletion.busy}
                    onClick={() =>
                      void deletion.submit(async () => {
                        await client.deleteSurvey(survey.id);
                        window.location.hash = href.job(survey.jobId);
                      })
                    }
                  >
                    {deletion.busy ? "Deleting…" : "Confirm delete"}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={deletion.busy}
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="secondary"
                  onClick={() => setConfirmingDelete(true)}
                >
                  Delete
                </button>
              )
            ) : null}
          </div>
        </div>
        <ProvenanceTally values={values} />
      </div>

      {photosOnly.error !== null ? (
        <Failure error={photosOnly.error} what="exporting the photographs" />
      ) : null}
      {exportModel.error !== null ? (
        <Failure error={exportModel.error} what="exporting this survey" />
      ) : null}
      {handOff.error !== null ? (
        <Failure error={handOff.error} what="bundling this survey for the other portal" />
      ) : null}
      {deletion.error !== null ? (
        <Failure error={deletion.error} what="deleting this survey" />
      ) : null}

      {sharing ? (
        <div id="survey-share-panel">
          <SurveySharePanel
            surveyId={survey.id}
            jobId={survey.jobId}
            onClose={() => setSharing(false)}
          />
        </div>
      ) : null}

      {/* Keyed on the last recorded change, so a page reload that brings back
          newer facts (someone else saved them) starts the card from those
          rather than from this browser's stale draft. Absent only against an
          API older than the property details, which could not save them. */}
      {survey.property !== undefined ? (
        <PropertyCard
          key={`${survey.id}:${survey.property.updatedAt ?? "never"}`}
          survey={survey}
          property={survey.property}
          me={me}
        />
      ) : null}

      <MarkComplete survey={survey} me={me} onCompleted={reload} />

      <SurveyWorkspace
        surveyId={survey.id}
        jobId={survey.jobId}
        me={me}
        planEdits={planEdits}
        canEditPlan={canEditPlan}
        figuresRevision={figuresRevision}
        onFiguresChanged={refreshFigures}
        heatLossRooms={current.rooms}
        canAnswerWalls={canAnswerWalls}
        validation={validation}
        onRoomChanged={reloadAndRefresh}
        withPhotos
      />

      <DesignReadinessCard
        surveyId={survey.id}
        jobId={survey.jobId}
        me={me}
        revision={figuresRevision}
        onChanged={refreshFigures}
      />

      <SiteSurveyCard surveyId={survey.id} />

      <DesignConditionsCard
        survey={survey}
        canEdit={can({ role: me.role, resource: "survey", action: "update", isAssigned: true }) && survey.status !== "superseded"}
        onChanged={refreshFigures}
      />
      <DefaultMaterialsCard
        surveyId={survey.id}
        revision={figuresRevision}
        canEdit={can({ role: me.role, resource: "survey", action: "update", isAssigned: true }) && survey.status !== "superseded"}
        onChanged={reloadAndRefresh}
      />
      <HeatLoss
        surveyId={survey.id}
        planEdits={planEdits}
        revision={figuresRevision}
        assumedOutsideWalls={outsideWallsToCheck.length}
        validation={validation}
      />
      <WholeHouseHeatLossByElement surveyId={survey.id} rooms={survey.rooms} revision={figuresRevision} />
      <Assumptions
        surveyId={survey.id}
        onConfirmed={reload}
        revision={figuresRevision}
        canConfirm={canEditPlan}
      />

      {/* Not labelled by its heading: the scan's rooms sidebar is already the
          "Rooms" landmark. The id is only for the heat loss's "Check them". */}
      <section>
        <h2 id="survey-rooms-heading">Rooms</h2>
        <OutsideWallsNotice
          count={outsideWallsToCheck.length}
          builtForm={survey.property?.builtForm ?? null}
          partyWalls={current.rooms.reduce(
            (n, room) => n + room.surfaces.filter((s) => s.kind === "wall" && s.adjacency === "party").length,
            0,
          )}
        />
        {current.rooms.map((room) => (
          <Room key={room.id} room={room} canAnswer={canAnswerWalls} onAnswered={refreshFigures} />
        ))}
      </section>

      <SpaceModelSection surveyId={survey.id} />
    </>
  );
}

/**
 * The MetaRoom-style workspace, rebuilt 2026-09-09 after Josh's blunt "not
 * five percent" verdict on the earlier grid.
 *
 * The visual argument here is that the 3D scan is the star: it fills the
 * middle of the page at 60vh, framed by a slim rooms sidebar on the left that
 * shows the *structure* of what was captured (Ground floor → Room 1) rather
 * than a wall-by-wall table. The wall-by-wall detail was the panic in the old
 * design — a 340px column shrinking six-column labels into five-line wraps —
 * and it belongs below the fold, as a drill-down on whichever room the
 * surveyor has clicked in the sidebar. That is what MetaRoom does, and it is
 * the shape a surveyor's eye actually wants.
 *
 * Room selection and element selection are held here so both the sidebar and
 * the below-fold detail agree on what's being looked at. Clicking an element
 * in the 3D view highlights its row in the walls table, and vice versa —
 * exactly as the old grid did, just relocated so the eye can actually follow
 * it.
 *
 * Photographs live in their own section below the room detail. Fetched at the
 * job because the API has no per-survey photo route yet; when one arrives,
 * swap the fetch here and nothing else changes.
 */
function SurveyWorkspace({
  surveyId,
  jobId,
  me,
  planEdits,
  canEditPlan,
  figuresRevision,
  onFiguresChanged,
  heatLossRooms,
  canAnswerWalls,
  validation,
  onRoomChanged,
  withPhotos,
}: {
  readonly surveyId: string;
  /** False on the drawing-only screen: photographs have a door of their own. */
  readonly withPhotos: boolean;
  readonly jobId: string;
  readonly me: Me;
  readonly planEdits: PlanEditsState;
  readonly canEditPlan: boolean;
  /** Bumped when the heat-loss rooms were re-read (after a calibration). */
  readonly figuresRevision: number;
  /** A calibration was applied or removed: the heat-loss rooms may have been re-converted. */
  readonly onFiguresChanged: () => void;
  /** The survey's heat-loss rooms, for what is on the other side of each scanned wall (0045). */
  readonly heatLossRooms: readonly SurveyRoom[];
  /** May answer "what is on the other side?" — `survey: update`, on a draft survey. */
  readonly canAnswerWalls: boolean;
  /** Computed once on the page above, so the count here matches the heat loss's. */
  readonly validation: ValidationReport;
  /** A room or surface panel saved: re-read the survey and the heat loss. */
  readonly onRoomChanged: () => void;
}): ReactNode {
  const { client } = usePortal();
  /**
   * WHICH FLOOR THE EDITOR IS WORKING ON.
   *
   * A survey combined from two walks (0069) holds a capture per floor, each in
   * its own coordinate frame — so the editor cannot hold "the survey's scan",
   * it holds one storey's at a time. Null until the floors are known, which
   * keeps the first load identical to what it always did for the overwhelming
   * majority of surveys: one floor, one scan, no parameter.
   */
  const [editingStorey, setEditingStorey] = useState<number | null>(null);
  const { state: storeysState } = useAsync<
    readonly { storey: number; label: string; hasScan: boolean }[]
  >(async () => client.surveyStoreys(surveyId), [client, surveyId]);
  const surveyStoreys = storeysState.status === "ready" ? storeysState.data : [];
  /**
   * Only offered when there is a choice to make. One floor is the normal case
   * and a picker with a single entry is furniture, not a control.
   */
  const drawableStoreys = surveyStoreys.filter((s) => s.hasScan);
  const { state: scanState, reload: reloadScan } = useAsync<SurveyScan>(
    () =>
      client.surveyScan(
        surveyId,
        editingStorey === null ? {} : { storey: editingStorey },
      ),
    [client, surveyId, editingStorey],
  );
  // Laser calibrations of the scan (0040). `async` so a client built before
  // the method existed fails this load, not the page — the lesson the tape
  // measurements section learned.
  const { state: calibrationsState, reload: reloadCalibrations } =
    useAsync<ScanCalibrationList | null>(
      async () => client.scanCalibrations(surveyId),
      [client, surveyId],
    );
  const { state: photosState } = useAsync<readonly PhotoEvidence[]>(
    () => client.jobPhotos(jobId),
    [client, jobId],
  );
  // The plan drawn by hand, for a survey with no scan (0047). The source is
  // memoised so the hook does not re-read on every render.
  const drawnSource = useMemo(() => drawnPlanFromApi(client, surveyId), [client, surveyId]);
  const drawnPlan = useDrawnPlan(drawnSource, surveyId);

  return (
    <>
      <ScanRegion
        surveyId={surveyId}
        scanState={scanState}
        reloadScan={reloadScan}
        photosState={photosState}
        planEdits={planEdits}
        canEditPlan={canEditPlan}
        calibrationsState={calibrationsState}
        reloadCalibrations={reloadCalibrations}
        figuresRevision={figuresRevision}
        onFiguresChanged={onFiguresChanged}
        heatLossRooms={heatLossRooms}
        canAnswerWalls={canAnswerWalls}
        validation={validation}
        onRoomChanged={onRoomChanged}
        drawnPlan={drawnPlan}
        storeyChoices={drawableStoreys}
        editingStorey={editingStorey}
        onEditStorey={setEditingStorey}
        withPhotos={withPhotos}
        // Asked of the matrix the API enforces; `isAssigned: true` for the
        // reason `MarkComplete` gives — this screen only renders for a survey
        // the caller could already read.
        canCalibrate={can({ role: me.role, resource: "survey", action: "update", isAssigned: true })}
        // Objects and the design inputs on them are `design: update`, at
        // organisation scope only; dimensions ride on `survey: update`. Both
        // exactly as the API enforces them, so an installer on the job sees
        // the plan with no tool that could only come back refused.
        canEditObjects={can({ role: me.role, resource: "design", action: "update" })}
        // Comments on the plan (0043), by the rules plan-comments.ts sets:
        // whoever sees the plan's design layer and may message colleagues
        // may comment; owner and admin may delete anyone's.
        commentViewer={{
          userId: me.userId,
          canComment:
            can({ role: me.role, resource: "design", action: "read", isAssigned: true }) &&
            can({ role: me.role, resource: "chat", action: "create" }),
          canModerate: can({ role: me.role, resource: "chat", action: "delete" }),
        }}
      />
    </>
  );
}

/**
 * The 3D scan and everything that hangs off it: a rooms sidebar on the left,
 * the model itself filling the middle, a room-detail block below with big
 * number cards for the headline measurements and per-wall / per-opening
 * tables under those.
 *
 * All three parts talk to each other. Picking a room in the sidebar drops the
 * detail block to that room and clears the element highlight — the previous
 * highlight was about the previous room's walls, so keeping it lit on a
 * different room's model would be lying about what's selected. Picking an
 * element in the 3D view or a row in the walls table sets the highlight and
 * moves the sidebar's active room to whichever room contains it, so the two
 * axes of selection never desynchronise.
 */
function ScanRegion({
  surveyId,
  scanState,
  reloadScan,
  photosState,
  planEdits,
  canEditPlan,
  calibrationsState,
  reloadCalibrations,
  figuresRevision,
  onFiguresChanged,
  heatLossRooms,
  canAnswerWalls,
  validation,
  onRoomChanged,
  canCalibrate,
  canEditObjects,
  commentViewer,
  drawnPlan,
  storeyChoices,
  editingStorey,
  onEditStorey,
  withPhotos,
}: {
  readonly surveyId: string;
  /** False on the drawing-only screen: photographs have a door of their own. */
  readonly withPhotos: boolean;
  /** Floors with a scan to draw. More than one only for a combined property. */
  readonly storeyChoices: readonly { storey: number; label: string; hasScan: boolean }[];
  /** Null until a floor is chosen: the survey's own scan, as before. */
  readonly editingStorey: number | null;
  readonly onEditStorey: (storey: number) => void;
  readonly heatLossRooms: readonly SurveyRoom[];
  /** The plan drawn by hand, where this survey has no scan (0047). */
  readonly drawnPlan: DrawnPlanState;
  readonly canAnswerWalls: boolean;
  readonly validation: ValidationReport;
  readonly onRoomChanged: () => void;
  readonly scanState: AsyncState<SurveyScan>;
  readonly reloadScan: () => void;
  readonly photosState: AsyncState<readonly PhotoEvidence[]>;
  readonly planEdits: PlanEditsState;
  readonly canEditPlan: boolean;
  readonly calibrationsState: AsyncState<ScanCalibrationList | null>;
  readonly reloadCalibrations: () => void;
  readonly figuresRevision: number;
  readonly onFiguresChanged: () => void;
  readonly canCalibrate: boolean;
  readonly canEditObjects: boolean;
  readonly commentViewer: PlanCommentViewer;
}): ReactNode {
  // The SCAN as stored: the evidence, and what a calibration's factor is
  // taken from. Never shown scaled and never scaled in place. Null for a
  // survey that was never scanned — which is what makes calibration
  // impossible below, and a hand-drawn plan possible.
  const scannedCapture: RoomPlanCapture | null =
    scanState.status === "ready" ? scanState.data.scan?.capture ?? null : null;
  // A survey with no scan and a plan drawn by hand reads that drawing as a
  // capture — the same pure function the API uses — so the plan, its
  // corrections, the objects on it, the 3D view and every table below are the
  // code they always were. The word "scan" is never used of it on screen.
  // Or nothing drawn yet but the surveyor has opened the plan to draw on — or
  // to trace over an estate agent's floor plan uploaded under it.
  const [drawOnScreen, setDrawOnScreen] = useState(false);
  /**
   * The floor-to-ceiling height for the next room, millimetres as typed —
   * shared by the panel beside the plan and the field on the plan itself, so
   * it is asked for once and typed once.
   */
  const [drawnCeilingMm, setDrawnCeilingMm] = useState("");
  const handDrawn =
    scannedCapture === null &&
    (drawnPlan.plan.rooms.length > 0 || (drawOnScreen && drawnPlan.status === "ready" && drawnPlan.canDraw));
  const drawnCapture = useMemo<RoomPlanCapture | null>(
    () => (handDrawn ? drawnPlanCapture(drawnPlan.plan) : null),
    [handDrawn, drawnPlan.plan],
  );
  const rawCapture: RoomPlanCapture | null = scannedCapture ?? drawnCapture;
  // Which storey the drawing panel is working on before anything is drawn —
  // once there are rooms, the plan's own storey selector takes over.
  const [drawnStorey, setDrawnStorey] = useState(0);
  /**
   * The height a room dragged out on this storey would be given: the height
   * of a room already on it, or the one typed for the purpose — and null
   * when there is neither, which is when the plan asks for it.
   *
   * Never zero. It was `?? 0` until 2026-09-29, and that made the FIRST room
   * on a storey impossible to draw: `validateDrawnPlan` refuses a room whose
   * height is outside 1.5–6 m, so the save was rejected, the plan was left
   * exactly as it was, and the drag appeared to do nothing. Without a first
   * room there was never a room to take a height from, so it could not come
   * right by itself — and in full screen the refusal was written on a panel
   * that is not on the page, leaving the correction layer's "a new wall needs
   * a room on this storey" as the only thing on screen to explain it.
   */
  const drawnCeilingM = useMemo(() => {
    const fromStorey = drawnPlan.plan.rooms.find((r) => r.story === drawnStorey)?.ceilingHeightM;
    const candidate = fromStorey ?? readMm(drawnCeilingMm);
    if (candidate === undefined) return null;
    return candidate >= DRAWN_CEILING_RANGE_M.min && candidate <= DRAWN_CEILING_RANGE_M.max
      ? candidate
      : null;
  }, [drawnPlan.plan.rooms, drawnStorey, drawnCeilingMm]);
  const rawGroups = useMemo<readonly ScanRoomGroup[]>(
    () => (rawCapture ? scanRoomGroups(rawCapture) : []),
    [rawCapture],
  );

  // The last list that loaded, held across a reload so applying or removing a
  // calibration does not flash every figure back to the scan's own.
  const lastCalibrations = useRef<ScanCalibrationList | null>(null);
  if (calibrationsState.status === "ready") lastCalibrations.current = calibrationsState.data;
  const calibrationList =
    calibrationsState.status === "ready" ? calibrationsState.data : lastCalibrations.current;
  const calibrationPhase: "loading" | "ready" | "failed" =
    calibrationsState.status === "failed"
      ? "failed"
      : calibrationsState.status === "loading" && lastCalibrations.current === null
        ? "loading"
        : "ready";

  // Every figure below — the plan, the 3D model, the sidebar, the tables — is
  // read from the calibrated copy. See packages/geometry/src/calibration.ts:
  // sizes scale, the plan's frame does not move, so objects placed on the
  // plan stay where they were put.
  const calibrated = useMemo<CalibratedCapture | null>(
    () => (rawCapture ? applyCalibration(rawCapture, appliedCalibrations(calibrationList)) : null),
    [rawCapture, calibrationList],
  );
  // The effective plan: the calibrated copy with the designers' corrections
  // (0039) laid over it — the capture itself is never changed. The plan, the
  // 3D model, the rooms list and the tables below all show this, so none of
  // them contradicts another. Calibration first, corrections on top: see
  // readEffectiveSurveyCapture in the API for why that order.
  const effective = useMemo(
    () => (calibrated ? applyPlanEdits(calibrated.capture, planEdits.records) : null),
    [calibrated, planEdits.records],
  );
  const groups = useMemo<readonly ScanRoomGroup[]>(
    () => (effective ? effectiveRoomGroups(effective) : []),
    [effective],
  );
  // The editor works over the calibrated copy: what "as scanned" and "revert
  // to scan" mean on the plan is the scan at its calibrated size.
  const editLayer = useMemo<PlanEditLayer | undefined>(
    () =>
      calibrated && effective && planEdits.status !== "unavailable"
        ? { capture: calibrated.capture, state: planEdits, plan: effective, canEdit: canEditPlan }
        : undefined,
    [calibrated, effective, planEdits, canEditPlan],
  );
  // What the tables mark: elements corrected on the plan, and those that moved
  // with a corrected wall (a window in it, a floor redrawn to it).
  const edited = useMemo<ReadonlyMap<string, EditMark> | undefined>(
    () =>
      effective && (effective.status.size > 0 || effective.followed.size > 0)
        ? new Map<string, EditMark>([
            ...[...effective.followed].map((id) => [id, "followed"] as const),
            ...effective.status,
          ])
        : undefined,
    [effective],
  );
  const correctionCount = planEdits.records.filter((r) => r.targetKind !== "room").length;
  // Each scanned wall's heat-loss twin, for the Walls table's "Other side".
  // The storey is passed because a merged floor's rooms are NOT called what
  // the capture calls them — see `heatLossWallFinder` for what that cost.
  const findHeatLossWall = useMemo(
    () => heatLossWallFinder(heatLossRooms, editingStorey),
    [heatLossRooms, editingStorey],
  );

  const [selectedElementId, setSelectedElementId] = useState<string | undefined>(
    undefined,
  );
  const [selectedRoomIndex, setSelectedRoomIndex] = useState(0);
  const [view, setView] = useState<ScanView>(rememberedScanView);

  const hiddenIds = useMemo<ReadonlySet<string>>(() => new Set(), []);

  // The plan's objects (outdoor unit, radiators, pipes, fittings) are saved
  // with the survey, not in this browser.
  const { client } = usePortal();
  const planObjects = useMemo(() => planObjectsFromApi(client, surveyId), [client, surveyId]);
  // The floor-plan picture under each storey, to trace over (0057).
  const planImages = useMemo(() => planImagesFromApi(client, surveyId), [client, surveyId]);
  // Whether internal walls and doors count (Design conditions): the wall panel says when they do not.
  const [countsInternal, setCountsInternal] = useState<boolean | null>(null);
  useEffect(() => {
    let live = true;
    // Through a promise, so a client without the call (an older API) is only a failure here.
    Promise.resolve()
      .then(() => client.designConditions(surveyId))
      .then((c) => live && setCountsInternal(c.countHeatBetweenRooms === true))
      .catch(() => live && setCountsInternal(null));
    return () => {
      live = false;
    };
  }, [client, surveyId, figuresRevision]);
  // Re-read when the heat-loss rooms were (it reads their heat loss per room).
  const designCheck = useMemo(
    () => designCheckFromApi(client, surveyId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, surveyId, figuresRevision],
  );
  const planDimensions = useMemo(() => planDimensionsFromApi(client, surveyId), [client, surveyId]);
  // Comments pinned to the plan (0043): on the plan, and their pins in 3D.
  const planComments = useMemo(() => planCommentsFromApi(client, surveyId), [client, surveyId]);

  // The storeys the export menu offers, lowest first.
  const storeys = useMemo(() => {
    const seen = new Map<number, string>();
    for (const group of groups) if (!seen.has(group.story)) seen.set(group.story, group.storyLabel);
    return [...seen.entries()].sort(([a], [b]) => a - b).map(([story, label]) => ({ story, label }));
  }, [groups]);

  const activeIndex = Math.min(selectedRoomIndex, Math.max(groups.length - 1, 0));
  const activeGroup: ScanRoomGroup | null = groups[activeIndex] ?? null;

  // The heat-loss room behind the room on the drawing, and the heat-loss
  // surface behind whatever is selected on it. Matched on the scan's own
  // identifiers, exactly as `heatLossWallFinder` matches a wall, so a room
  // added by hand (which has none) simply has no panel rather than borrowing
  // another room's.
  const activeRoom = useMemo(
    () => heatLossRoomFor(heatLossRooms, activeGroup?.externalRef, editingStorey),
    [activeGroup, heatLossRooms, editingStorey],
  );
  const activeSurface = useMemo(
    () =>
      selectedElementId === undefined
        ? undefined
        : activeRoom?.surfaces.find((s) => s.externalRef === selectedElementId),
    [activeRoom, selectedElementId],
  );
  const activeElement = useMemo(
    () => activeGroup?.elements.find((e) => e.id === selectedElementId),
    [activeGroup, selectedElementId],
  );

  /**
   * Objects drawn on the plan that are not against any wall.
   *
   * The same check `@sts/calc` runs for the plan's own fit tags
   * (`listFitStatus`, note kind "free-standing"), read here from the plan
   * rooms the drawing is built from — so the list beside the plan and the tag
   * on the plan are the same judgement, not two that may drift apart.
   */
  // `async` deliberately, as the calibrations load above is: a client built
  // before this method existed throws "not a function" SYNCHRONOUSLY, and a
  // synchronous throw inside the effect takes the whole page down instead of
  // this one load. Wrapped, it lands in the failed state, and the `ready`
  // check below then reports no free-standing objects — which only ever
  // affects warnings, never the blocking count.
  const { state: objectsState } = useAsync(
    async () => client.planObjects(surveyId),
    [client, surveyId, figuresRevision],
  );
  const planRooms = useMemo(() => scanPlanRooms(groups), [groups]);
  const unsnapped = useMemo<readonly UnsnappedObject[]>(
    () =>
      objectsState.status === "ready" ? unsnappedObjects(objectsState.data, planRooms) : [],
    [objectsState, planRooms],
  );
  const report = useMemo(
    () => withUnsnappedObjects(validation, heatLossRooms, unsnapped),
    [validation, heatLossRooms, unsnapped],
  );

  /**
   * Take me to the thing this problem is about.
   *
   * `selectId` may name a wall or a floor (an element on the drawing) or a
   * room; both are scan identifiers and a room's is never an element's, so
   * trying the element first and the room second is unambiguous. Falling back
   * to the room's NAME covers a room typed in by hand, which has no scan
   * identifier at all — it has no element to light up, so the sidebar moving
   * to it is the whole of what "go to it" can mean.
   */
  /** The plan, so a check clicked in the side column can scroll it into view. */
  const planSection = useRef<HTMLElement | null>(null);

  /**
   * Take me to the thing this check is about.
   *
   * SELECTING WAS NEVER THE PROBLEM — the right wall has been highlighted on
   * the plan since this was written. What it did not do was SHOW you: the
   * checks are a long column beside the plan, so somebody scrolled down among
   * twenty warnings clicked one, the plan silently selected a wall several
   * screens above, and nothing appeared to happen at all ("guide for warnings
   * in this section are not clearly working", 2026-10-08).
   *
   * So the plan is brought back into view after the selection. Smooth rather
   * than a jump, because the point is to show WHERE the thing is — a jump
   * leaves you somewhere new with no idea how you got there — and honouring
   * the reader's own motion preference, since a long smooth scroll is exactly
   * what makes some people ill.
   */
  const showThePlan = (): void => {
    const node = planSection.current;
    if (!node?.scrollIntoView) return;
    const still = globalThis.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches === true;
    node.scrollIntoView({ behavior: still ? "auto" : "smooth", block: "start" });
  };

  const goTo = (problem: ValidationProblem): void => {
    showThePlan();
    const ref = problem.selectId;
    if (ref !== null) {
      if (groups.some((group) => group.elements.some((el) => el.id === ref))) {
        pickElement(ref);
        return;
      }
      const roomIndex = groups.findIndex((group) => group.externalRef === ref);
      if (roomIndex >= 0) {
        pickRoom(roomIndex);
        return;
      }
    }
    const byName = groups.findIndex((group) => group.label === problem.roomName);
    if (byName >= 0) pickRoom(byName);
  };

  // A pick from the 3D view carries no room context — resolve it here so the
  // sidebar's active-room indicator follows the model rather than lagging it.
  function pickElement(id: string | undefined): void {
    setSelectedElementId(id);
    if (id === undefined) return;
    const containing = groups.findIndex((group) =>
      group.elements.some((el) => el.id === id),
    );
    if (containing >= 0) setSelectedRoomIndex(containing);
  }

  function pickRoom(index: number): void {
    setSelectedRoomIndex(index);
    setSelectedElementId(undefined);
  }

  function chooseView(next: ScanView): void {
    setView(next);
    rememberScanView(next);
  }

  // "Full screen": the plan (or the model) fills the window, a CAD-style
  // workspace — the page behind it held still.
  const [fullScreen, setFullScreen] = useState(false);
  const selectedRef = useRef(selectedElementId);
  selectedRef.current = selectedElementId;
  useEffect(() => {
    if (!fullScreen) return;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if ((event.target as Element | null)?.closest?.("input, textarea, select")) return;
      // Escape belongs first to whatever is in hand — a tool, a half-drawn
      // line, a drag — and each of those says so (preventDefault). Asked after
      // every listener has had it; then a selection is let go; only then does
      // the plan leave full screen. Esc, Esc: as in a CAD program.
      setTimeout(() => {
        if (event.defaultPrevented) return;
        if (selectedRef.current !== undefined) setSelectedElementId(undefined);
        else setFullScreen(false);
      }, 0);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [fullScreen]);
  // Which way up the plan is drawn (0055): squared to its walls, the front at
  // the bottom along X — the survey's saved "Rotate 90°", else the entrance
  // door. The 3D view turns the same way. `async` for the reason the
  // calibrations load gives: an older client fails this load, not the page.
  const { state: orientationState } = useAsync(
    async () => client.planOrientation(surveyId),
    [client, surveyId],
  );
  const [turnedTo, setTurnedTo] = useState<number | null | undefined>(undefined);
  const quarterTurns =
    turnedTo !== undefined
      ? turnedTo
      : orientationState.status === "ready"
        ? orientationState.data.quarterTurns
        : null;
  const planTurnDeg = useMemo(() => planOrientation(planRooms, quarterTurns).turnDeg, [planRooms, quarterTurns]);
  const turnPlan = (next: number): void => {
    setTurnedTo(next);
    // Refused (a superseded survey, a lost connection): back to what is saved.
    void client.setPlanOrientation(surveyId, next).catch(() => setTurnedTo(undefined));
  };

  const fullScreenButton = (
    <button
      type="button"
      className="survey-scan__fullscreen"
      aria-pressed={fullScreen}
      onClick={() => setFullScreen((on) => !on)}
      title={fullScreen ? "Back to the survey page (Esc)" : "Fill the window with the plan"}
      data-testid="scan-fullscreen"
    >
      {fullScreen ? "Exit full screen" : "Full screen"}
    </button>
  );

  return (
    <section
      ref={planSection}
      className="survey-scan"
      aria-label={handDrawn ? "Hand-drawn plan and measurements" : "Scan and measurements"}
    >
      <div className="survey-scan__grid">
        <RoomsSidebar
          groups={groups}
          calibration={calibrated?.rooms ?? []}
          selectedIndex={activeIndex}
          onSelect={pickRoom}
        />

        {/* The plan and the model share this one frame and the one
            selection above: a wall picked on either — or in a table below —
            is lit on all three. A toggle rather than two panels side by side,
            because the frame is the widest thing on the page and halving it
            would make both views worse. */}
        <div
          className={[
            "survey-scan__viewer",
            view === "plan" ? "survey-scan__viewer--plan" : "",
            fullScreen ? "is-fullscreen" : "",
          ]
            .filter(Boolean)
            .join(" ")}
          {...(fullScreen ? { role: "dialog", "aria-modal": true } : {})}
          aria-label={
            view === "plan" ? "Floor plan" : handDrawn ? "3D view of the drawn rooms" : "3D scan"
          }
        >
          <Async state={scanState} what="the scan" reload={reloadScan}>
            {(result) =>
              result.scan === null && !handDrawn ? (
                <div className="survey-scan__empty">
                  <p className="empty">
                    No 3D scan has been synced for this survey yet. The model
                    and its measurements appear here once the surveyor&apos;s
                    capture uploads.
                  </p>
                  {/* No scan, and there may never be one: an older phone, a
                      refused scan, a plan taken off a drawing. The surveyor
                      draws the plan instead, and it drives the heat loss the
                      same way a scan would. */}
                  {drawnPlan.status === "ready" && drawnPlan.canDraw && canEditPlan ? (
                    <p>
                      <button
                        type="button"
                        className="primary"
                        data-testid="draw-on-plan"
                        onClick={() => {
                          setView("plan");
                          setDrawOnScreen(true);
                        }}
                      >
                        Draw the plan on screen — or trace over a floor-plan picture
                      </button>
                    </p>
                  ) : null}
                  {drawnPlan.status === "ready" && drawnPlan.canDraw && canEditPlan ? (
                    <DrawPlanPanel
                      state={drawnPlan}
                      storey={drawnStorey}
                      onStoreyChange={setDrawnStorey}
                      heightMm={drawnCeilingMm}
                      onHeightMmChange={setDrawnCeilingMm}
                      canEdit
                    />
                  ) : null}
                </div>
              ) : (
                <>
                  {view === "plan" ? (
                    <FloorPlan
                      surveyId={surveyId}
                      groups={groups}
                      selectedId={selectedElementId}
                      onSelect={pickElement}
                      objects={planObjects}
                      images={planImages}
                      design={designCheck}
                      dimensions={planDimensions}
                      comments={planComments}
                      commentViewer={commentViewer}
                      canEditObjects={canEditObjects}
                      canEditDimensions={canEditPlan}
                      {...(editLayer ? { edits: editLayer } : {})}
                      {...(handDrawn
                        ? { storey: drawnStorey, onStoreyChange: setDrawnStorey }
                        : {})}
                      {...(handDrawn && canEditPlan
                        ? {
                            drawing: {
                              canDraw: drawnPlan.canDraw,
                              minSideM: DRAWN_MIN_SIDE_M,
                              newRoomCeilingM: drawnCeilingM,
                              ceilingMm: drawnCeilingMm,
                              onCeilingMmChange: setDrawnCeilingMm,
                              onDrawRoom: (storeyDrawn, a, b) => {
                                // The plan will not offer the drag without a
                                // height, so this is only ever a figure the
                                // surveyor gave; nothing is invented either way.
                                if (drawnCeilingM === null) return;
                                const room = roomFromCorners(
                                  drawnPlan.plan,
                                  storeyDrawn,
                                  a,
                                  b,
                                  `Room ${drawnPlan.plan.rooms.length + 1}`,
                                  drawnCeilingM,
                                );
                                if (room) {
                                  drawnPlan.update({
                                    ...drawnPlan.plan,
                                    rooms: [...drawnPlan.plan.rooms, room],
                                  });
                                }
                              },
                            },
                          }
                        : {})}
                      // In the plan's own top row, beside its tools, so the
                      // two wrap rather than overlap (the Export button used
                      // to cover "Wall sizes" at 1440px). Exports are of the
                      // plan, so they sit on the plan only.
                      toolbarLead={
                        <>
                          <ScanViewToggle value={view} onChange={chooseView} />
                          {/* Which floor's scan the editor is open on. Shown
                              only when the survey holds more than one — a
                              picker with a single entry is furniture. Each
                              floor is a separate walk in its own frame, so
                              switching reloads the geometry rather than
                              panning within one drawing. */}
                          {storeyChoices.length > 1 ? (
                            <label className="plan-storey-pick">
                              Floor
                              <select
                                value={editingStorey ?? storeyChoices[0]!.storey}
                                onChange={(e) => onEditStorey(Number(e.target.value))}
                              >
                                {storeyChoices.map((s) => (
                                  <option key={s.storey} value={s.storey}>
                                    {s.label}
                                  </option>
                                ))}
                              </select>
                            </label>
                          ) : null}
                          <PlanExportMenu surveyId={surveyId} storeys={storeys} />
                          {fullScreenButton}
                        </>
                      }
                      fullScreen={fullScreen}
                      orientation={{ quarterTurns, ...(canEditPlan ? { onTurn: turnPlan } : {}) }}
                    />
                  ) : (
                    <>
                      <div className="survey-scan__controls">
                        <ScanViewToggle value={view} onChange={chooseView} />
                        {fullScreenButton}
                      </div>
                      <ScanViewerWithComments
                        surveyId={surveyId}
                        turnDeg={planTurnDeg}
                        groups={groups}
                        {...(selectedElementId ? { selectedId: selectedElementId } : {})}
                        hiddenIds={hiddenIds}
                        onSelect={pickElement}
                        comments={planComments}
                        objects={planObjects}
                      />
                      {correctionCount > 0 ? (
                        <p className="survey-scan__edited-note" data-testid="scan-3d-edited-note">
                          Showing the {handDrawn ? "drawing" : "scan"} with {correctionCount}{" "}
                          {correctionCount === 1 ? "correction" : "corrections"} from the floor plan.
                        </p>
                      ) : null}
                      <div className="survey-scan__axes" aria-hidden="true">
                        <span className="survey-scan__axis survey-scan__axis--x">x</span>
                        <span className="survey-scan__axis survey-scan__axis--y">y</span>
                        <span className="survey-scan__axis survey-scan__axis--z">z</span>
                      </div>
                    </>
                  )}
                </>
              )
            }
          </Async>
        </div>

        {/* Beside the drawing, not under it: the count is only useful while
            you are looking at the thing it counts, and each row selects its
            room or wall on the plan, in the 3D view and in the tables. */}
        <div className="survey-scan__checks">
          <ValidationList report={report} onGoTo={goTo} />

          {/* Drawing the plan by hand (0047): the rooms this storey holds, with
              their names, their wall lengths in millimetres and the storeys
              themselves. Beside the plan rather than on it, because everything
              in it is a number to type — only a room's PLACE is a gesture. */}
          {handDrawn && drawnPlan.status === "ready" ? (
            <DrawPlanPanel
              state={drawnPlan}
              storey={drawnStorey}
              onStoreyChange={setDrawnStorey}
              heightMm={drawnCeilingMm}
              onHeightMmChange={setDrawnCeilingMm}
              canEdit={canEditPlan && drawnPlan.canDraw}
            />
          ) : null}

        </div>
      </div>

      {/* THE PHOTOGRAPHS, FULL WIDTH AND ON THEIR OWN.
          They used to sit in the narrow column beside the plan, sharing it
          with the compliance checks, where a rule forced one tile per row —
          a filmstrip, which is fine for three photographs and a tunnel for
          twenty-eight. A survey here carries 7 to 28 of them, so the column
          scrolled in both directions and the captions were cut off mid-word
          ("show all photos at a time on the screen not like clumsy",
          2026-10-08). Given the page's whole width they lay out as a grid and
          a survey's photographs are visible at a glance, which is what
          somebody checking evidence actually wants. */}
      {/* NOT ON THE DRAWING SCREEN, where they are somebody else's errand. A
          door labelled "The drawing" that scrolls into twenty-eight
          photographs is the everything-screen again under a new name; they
          keep their own door off the survey's overview. */}
      {withPhotos ? (
        <Async state={photosState} what="the photographs">
          {(photos) => <PhotoRail photos={photos} />}
        </Async>
      ) : null}

      {activeGroup !== null ? (
        <RoomDetail
          group={activeGroup}
          selectedElementId={selectedElementId}
          onSelectElement={pickElement}
          {...(edited ? { edited } : {})}
          calibration={
            // Only ever offered for a SCAN. A laser reading rescales what a
            // sensor measured; there is nothing in a hand-drawn plan to
            // rescale, and the API refuses one too.
            scannedCapture !== null ? (
              <CalibrationPanel
                surveyId={surveyId}
                rawCapture={scannedCapture}
                rawGroups={rawGroups}
                roomIndex={activeIndex}
                status={calibrated?.rooms[activeIndex] ?? null}
                calibrations={calibrationList}
                calibrationsState={calibrationPhase}
                selectedElementId={selectedElementId}
                canCalibrate={canCalibrate}
                onChanged={() => {
                  reloadCalibrations();
                  onFiguresChanged();
                }}
              />
            ) : null
          }
          calibrationFactor={calibrated?.rooms[activeIndex] ?? null}
          floorFromWalls={
            effective?.floors.some((f) => f.outcome === "redrawn" && f.roomRef === activeGroup.externalRef) ?? false
          }
          otherSide={{
            find: (wallId) => findHeatLossWall(activeGroup.externalRef, wallId),
            canAnswer: canAnswerWalls,
            onAnswered: onFiguresChanged,
          }}
          {...(activeRoom ? { room: activeRoom } : {})}
          {...(activeRoom
            ? { heatLoss: <RoomHeatLossBreakdown surveyId={surveyId} room={activeRoom} revision={figuresRevision} /> }
            : {})}
          {...(activeSurface ? { surface: activeSurface } : {})}
          {...(activeElement && activeElement.kind === "wall"
            ? {
                surfaceLengthM: wallLengthM(activeElement.areaM2, activeElement.heightM),
                surfaceHeightM: activeElement.heightM,
              }
            : {})}
          canEditRoom={canAnswerWalls}
          onRoomChanged={onRoomChanged}
          internalNotCounted={countsInternal === false}
          {...(activeRoom
            ? {
                fabric: (
                  <RoomFloorCeiling
                    key={activeRoom.id}
                    surveyId={surveyId}
                    room={activeRoom}
                    canEdit={canAnswerWalls}
                    onSaved={onRoomChanged}
                  />
                ),
              }
            : {})}
          {...(activeGroup.externalRef
            ? {
                notes: (
                  <RoomNotes
                    key={activeGroup.externalRef}
                    surveyId={surveyId}
                    roomRef={activeGroup.externalRef}
                    roomName={activeGroup.label}
                    {...(activeRoom ? { heatLossRoomId: activeRoom.id } : {})}
                    photos={photosState.status === "ready" ? photosState.data : []}
                    canEdit={canAnswerWalls}
                    revision={figuresRevision}
                    onFiguresChanged={onFiguresChanged}
                  />
                ),
              }
            : {})}
        />
      ) : null}
    </section>
  );
}

type ScanView = "3d" | "plan";

const SCAN_VIEW_KEY = "sts.survey-scan-view.v1";

/**
 * Which view the surveyor last chose, per browser. A convenience only — every
 * access is guarded because storage throws in some private modes, and the
 * answer then is simply the 3D view.
 */
function rememberedScanView(): ScanView {
  try {
    return globalThis.localStorage?.getItem(SCAN_VIEW_KEY) === "plan" ? "plan" : "3d";
  } catch {
    return "3d";
  }
}

function rememberScanView(view: ScanView): void {
  try {
    globalThis.localStorage?.setItem(SCAN_VIEW_KEY, view);
  } catch {
    // Not remembered; nothing else depends on it.
  }
}

/** Plan / 3D, as a two-button segmented control floating over the frame. */
function ScanViewToggle({
  value,
  onChange,
}: {
  readonly value: ScanView;
  readonly onChange: (view: ScanView) => void;
}): ReactNode {
  const options: readonly { readonly view: ScanView; readonly label: string }[] = [
    { view: "plan", label: "Plan" },
    { view: "3d", label: "3D" },
  ];
  return (
    <div className="survey-scan__view-toggle" role="group" aria-label="Scan view">
      {options.map((option) => (
        <button
          key={option.view}
          type="button"
          className="survey-scan__view-option"
          aria-pressed={value === option.view}
          onClick={() => onChange(option.view)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The structural navigator: floors, then rooms within each floor.
 *
 * A slim vertical list with the currently selected room highlighted. It also
 * carries the survey-wide totals at the top so the surveyor sees the sum of
 * what was captured without having to read a room detail. Empty state — no
 * scan — is a single quiet line, not a full-height placeholder, because the
 * viewer beside it already carries the empty message.
 */
function RoomsSidebar({
  groups,
  calibration,
  selectedIndex,
  onSelect,
}: {
  readonly groups: readonly ScanRoomGroup[];
  /** `applyCalibration(...).rooms`, index for index with `groups`. */
  readonly calibration: readonly RoomCalibration[];
  readonly selectedIndex: number;
  readonly onSelect: (index: number) => void;
}): ReactNode {
  const byStory = useMemo(() => {
    const map = new Map<string, ScanRoomGroup[]>();
    for (const group of groups) {
      const list = map.get(group.storyLabel) ?? [];
      list.push(group);
      map.set(group.storyLabel, list);
    }
    return Array.from(map.entries());
  }, [groups]);

  const totalFloor = groups.reduce(
    (sum, g) => sum + (typeof g.floorAreaM2 === "number" ? g.floorAreaM2 : 0),
    0,
  );
  const totalWallNet = groups.reduce((sum, g) => sum + g.netWallAreaM2, 0);
  const totalWallGross = groups.reduce((sum, g) => sum + g.wallAreaM2, 0);

  return (
    <aside className="survey-rooms" aria-label="Rooms">
      <p className="survey-rooms__title">Rooms</p>
      {groups.length === 0 ? (
        <p className="muted small">No rooms yet.</p>
      ) : (
        <>
          <ol className="survey-rooms__list">
            {byStory.map(([storyLabel, storyGroups]) => (
              <li key={storyLabel} className="survey-rooms__story">
                <p className="survey-rooms__story-label">{storyLabel}</p>
                <ol>
                  {storyGroups.map((group) => {
                    const active = group.index === selectedIndex;
                    return (
                      <li key={group.index}>
                        <button
                          type="button"
                          className="survey-rooms__pick"
                          aria-pressed={active}
                          onClick={() => onSelect(group.index)}
                        >
                          <span className="survey-rooms__pick-label">
                            {group.label}
                            {calibration[group.index] && calibration[group.index]!.source !== "none" ? (
                              <span
                                className="survey-rooms__cal"
                                title={`Calibrated ${formatCalibrationFactor(calibration[group.index]!.factor)} from a laser reading`}
                              >
                                {" "}
                                laser
                              </span>
                            ) : null}
                          </span>
                          <span className="survey-rooms__pick-metric">
                            {typeof group.floorAreaM2 === "number"
                              ? formatArea(group.floorAreaM2, "m")
                              : "—"}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ol>
              </li>
            ))}
          </ol>
          <dl className="survey-rooms__totals">
            <div>
              <dt>Total floor</dt>
              <dd>{formatArea(totalFloor, "m")}</dd>
            </div>
            <div>
              <dt>Total wall (net)</dt>
              <dd>
                {formatArea(totalWallNet, "m")}
                <span className="muted small">
                  {" "}
                  gross {formatArea(totalWallGross, "m")}
                </span>
              </dd>
            </div>
          </dl>
        </>
      )}
    </aside>
  );
}

/**
 * The picked room, opened out.
 *
 * The three headline numbers — floor area, net wall area, ceiling height —
 * are the ones a designer stands on, so they render as large-number cards at
 * the top, each with a quiet footnote that carries the caveat (openings
 * subtracted, ceiling derived from tallest wall). The per-wall and per-opening
 * tables live below the cards, no longer crammed into a 340px column. A
 * click in either table still highlights the element in the 3D view.
 */
function RoomDetail({
  group,
  selectedElementId,
  onSelectElement,
  edited,
  calibration,
  calibrationFactor,
  floorFromWalls = false,
  otherSide,
  room,
  surface,
  surfaceLengthM,
  surfaceHeightM,
  canEditRoom = false,
  onRoomChanged,
  heatLoss,
  notes,
  fabric,
  internalNotCounted = false,
}: {
  readonly group: ScanRoomGroup;
  readonly selectedElementId: string | undefined;
  readonly onSelectElement: (id: string | undefined) => void;
  /** Elements corrected on the floor plan: their figures are the designer's, not the scanner's. */
  readonly edited?: ReadonlyMap<string, EditMark>;
  /** The laser calibration block: where this room's figures come from, and the way to correct them. */
  readonly calibration?: ReactNode;
  /** This room's calibration, when its figures below are calibrated. */
  readonly calibrationFactor?: RoomCalibration | null;
  /** The room's floor was redrawn to its walls as corrected on the plan (0039). */
  readonly floorFromWalls?: boolean;
  /**
   * What is on the other side of each wall (0045): the heat-loss wall a
   * scanned wall became, and whether this viewer may answer for it. The
   * Walls table then shows the same "Other side" cell as the Rooms tables.
   */
  readonly otherSide?: {
    readonly find: (wallId: string) => HeatLossWall | undefined;
    readonly canAnswer: boolean;
    readonly onAnswered: () => void;
  };
  /**
   * The heat-loss room behind this scanned room, when the survey has one. The
   * room panel: type, name, internal temperature, air changes, unheated.
   */
  readonly room?: SurveyRoom;
  /** The heat-loss surface behind the selected element: its own panel. */
  readonly surface?: SurveySurface;
  /** The selected wall's length and height as the plan gives them. Shown, not saved. */
  readonly surfaceLengthM?: number | null;
  readonly surfaceHeightM?: number | null;
  /** `survey: update`, on a draft survey — the same test as answering a wall. */
  readonly canEditRoom?: boolean;
  readonly onRoomChanged?: () => void;
  /** Where this room's watts go, element by element (RoomHeatLossBreakdown). */
  readonly heatLoss?: ReactNode;
  /** The room's own photos and notes (RoomNotes). */
  readonly notes?: ReactNode;
  /** What is under the room and over it (RoomFloorCeiling). */
  readonly fabric?: ReactNode;
  /** The survey takes internal walls and doors at U = 0. */
  readonly internalNotCounted?: boolean;
}): ReactNode {
  const calibratedNote =
    calibrationFactor && calibrationFactor.source !== "none"
      ? `calibrated ${formatCalibrationFactor(calibrationFactor.factor)}`
      : null;
  const walls = group.elements.filter((e) => e.kind === "wall");
  // Only when the heat loss knows at least one of these walls: a scan not
  // (yet) converted into rooms has no other side to show.
  const showOtherSide = otherSide !== undefined && walls.some((w) => otherSide.find(w.id) !== undefined);
  const openings = group.elements.filter(
    (e) => e.kind === "window" || e.kind === "door" || e.kind === "opening",
  );
  const floors = group.elements.filter((e) => e.kind === "floor");

  return (
    <div className="survey-room" aria-label={`Detail for ${group.label}`}>
      <header className="survey-room__header">
        <div>
          <p className="survey-room__eyebrow">{group.storyLabel}</p>
          <h3 className="survey-room__title">{group.label}</h3>
        </div>
        <p className="muted small survey-room__hint">
          Pick an element on the plan, in the 3D view or in a table to
          highlight it in all three.
        </p>
      </header>

      {/* The property panels, beside the drawing and above the measured
          figures: what a person DECIDES about this room and this wall comes
          first, because the numbers below are all consequences of it. */}
      {room !== undefined ? (
        <div className="survey-room__panels">
          <RoomPanel
            room={room}
            canEdit={canEditRoom}
            onSaved={onRoomChanged ?? (() => {})}
            onSelectSurface={onSelectElement}
          />
          {surface !== undefined ? (
            <SurfacePanel
              surface={surface}
              roomName={room.name}
              canEdit={canEditRoom}
              onSaved={onRoomChanged ?? (() => {})}
              {...(surfaceLengthM !== undefined ? { lengthM: surfaceLengthM } : {})}
              {...(surfaceHeightM !== undefined ? { heightM: surfaceHeightM } : {})}
              internalNotCounted={internalNotCounted}
            />
          ) : (
            <p className="muted small survey-room__panels-hint">
              Pick a wall or a floor on the plan to set what it is made of and what
              is on the other side.
            </p>
          )}
        </div>
      ) : null}

      {fabric}
      {heatLoss}
      {notes}

      {calibration}

      <div className="survey-cards">
        <NumberCard
          label="Floor area"
          value={
            typeof group.floorAreaM2 === "number"
              ? formatArea(group.floorAreaM2, "m")
              : "—"
          }
          footnote={
            typeof group.floorAreaM2 === "number"
              ? floorFromWalls
                ? "inside the walls as corrected on the floor plan"
                : calibratedNote
                  ? `scanned floor, ${calibratedNote}`
                  : "measured from the scanned floor"
              : "no floor captured"
          }
        />
        <NumberCard
          label="Wall area, net"
          value={formatArea(group.netWallAreaM2, "m")}
          footnote={`gross ${formatArea(
            group.wallAreaM2,
            "m",
          )}, less ${formatArea(group.openingsAreaM2, "m")} of openings`}
        />
        <NumberCard
          label="Ceiling height"
          value={
            typeof group.ceilingHeightM === "number"
              ? formatLength(group.ceilingHeightM, "m")
              : "—"
          }
          info={
            typeof group.ceilingHeightM === "number"
              ? "Derived from the tallest wall — RoomPlan captures no ceiling."
              : undefined
          }
          footnote={
            typeof group.ceilingHeightM === "number" ? "derived" : "not captured"
          }
        />
      </div>

      {walls.length > 0 && (
        <ElementTable
          caption={`Walls in ${group.label}, with openings subtracted`}
          heading="Walls"
          {...(calibratedNote ? { note: calibratedNote } : {})}
          columns={
            showOtherSide
              ? ["Wall", "Width", "Height", "Area", "Openings", "Net", "Other side"]
              : ["Wall", "Width", "Height", "Area", "Openings", "Net"]
          }
          textColumns={["Other side"]}
          rows={walls}
          selectedElementId={selectedElementId}
          onSelectElement={onSelectElement}
          {...(edited ? { edited } : {})}
          renderRow={(wall) => (
            <>
              <td className="n">{formatLength(wall.widthM, "m")}</td>
              <td className="n">{formatLength(wall.heightM, "m")}</td>
              <td className="n">{formatArea(wall.areaM2, "m")}</td>
              <td className="n">
                {wall.openingsAreaM2 > 0 ? (
                  <>
                    − {formatArea(wall.openingsAreaM2, "m")}{" "}
                    <span className="muted small">
                      ({wall.openingIds.length}{" "}
                      {wall.openingIds.length === 1 ? "opening" : "openings"})
                    </span>
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
              <td className="n">
                <strong>{formatArea(wall.netAreaM2, "m")}</strong>
              </td>
              {showOtherSide ? <OtherSideCell wallId={wall.id} otherSide={otherSide!} /> : null}
            </>
          )}
        />
      )}

      {openings.length > 0 && (
        <ElementTable
          caption={`Windows, doors and openings in ${group.label}`}
          heading="Openings"
          {...(calibratedNote ? { note: calibratedNote } : {})}
          columns={["Opening", "Type", "Width", "Height", "Area"]}
          textColumns={["Type"]}
          rows={openings}
          selectedElementId={selectedElementId}
          onSelectElement={onSelectElement}
          {...(edited ? { edited } : {})}
          renderRow={(opening) => (
            <>
              <td>{kindLabel(opening)}</td>
              <td className="n">{formatLength(opening.widthM, "m")}</td>
              <td className="n">{formatLength(opening.heightM, "m")}</td>
              <td className="n">{formatArea(opening.areaM2, "m")}</td>
            </>
          )}
        />
      )}

      {floors.length > 0 && (
        <ElementTable
          caption={`Floors in ${group.label}`}
          heading="Floors"
          {...(calibratedNote ? { note: calibratedNote } : {})}
          columns={["Floor", "Width", "Depth", "Area"]}
          rows={floors}
          selectedElementId={selectedElementId}
          onSelectElement={onSelectElement}
          {...(edited ? { edited } : {})}
          renderRow={(floor) => (
            <>
              <td className="n">{formatLength(floor.widthM, "m")}</td>
              <td className="n">{formatLength(floor.heightM, "m")}</td>
              <td className="n">{formatArea(floor.areaM2, "m")}</td>
            </>
          )}
        />
      )}
    </div>
  );
}

/**
 * A single headline number, rendered large.
 *
 * The value dominates (28-32px, tabular figures so it doesn't dance between
 * rooms), the label sits quiet above it (11px uppercase muted), and the
 * footnote carries the caveat in one grey line. When `info` is set the label
 * gets an ⓘ affordance whose title tooltip carries the longer explanation —
 * so the reader sees the number first, the caveat second, the full story
 * only on hover.
 */
function NumberCard({
  label,
  value,
  footnote,
  info,
}: {
  readonly label: string;
  readonly value: string;
  readonly footnote?: string;
  readonly info?: string;
}): ReactNode {
  return (
    <div className="survey-card">
      <p className="survey-card__label">
        {label}
        {info ? (
          <span className="survey-card__info" title={info} aria-label={info}>
            ⓘ
          </span>
        ) : null}
      </p>
      <p className="survey-card__value">{value}</p>
      {footnote ? <p className="survey-card__footnote">{footnote}</p> : null}
    </div>
  );
}

/**
 * A table of scan elements — walls, openings or floors — with row-level
 * selection tied to whichever element is highlighted in the 3D view. The
 * heading and caption differ per kind but the shape is identical, so it lives
 * behind one component rather than three copies with slightly different
 * markup.
 */
function ElementTable({
  caption,
  heading,
  note,
  columns,
  textColumns = [],
  rows,
  selectedElementId,
  onSelectElement,
  renderRow,
  edited,
}: {
  readonly caption: string;
  readonly heading: string;
  /** Where the figures come from, when not the scan alone — "calibrated ×1.0071". */
  readonly note?: string;
  readonly columns: readonly string[];
  /**
   * Columns that hold words rather than measurements. Everything else after
   * the row header is right-aligned, because a column of areas is read by
   * comparing magnitudes down it and a ragged right edge makes that arithmetic
   * the reader's problem.
   */
  readonly textColumns?: readonly string[];
  readonly rows: readonly ScanElement[];
  readonly selectedElementId: string | undefined;
  readonly onSelectElement: (id: string | undefined) => void;
  readonly renderRow: (element: ScanElement) => ReactNode;
  readonly edited?: ReadonlyMap<string, EditMark>;
}): ReactNode {
  return (
    <div className="survey-element-table">
      <h4 className="survey-element-table__heading">
        {heading} <span className="muted">· {rows.length}</span>
        {note ? <span className="muted small"> · {note}</span> : null}
      </h4>
      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">{caption}</caption>
          <thead>
            <tr>
              {columns.map((col, index) => (
                <th
                  key={col}
                  scope="col"
                  className={
                    index === 0 || textColumns.includes(col) ? undefined : "n"
                  }
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((element) => (
              <tr
                key={element.id}
                className={
                  selectedElementId === element.id
                    ? "survey-element-table__row is-selected"
                    : "survey-element-table__row"
                }
              >
                <th scope="row">
                  <button
                    type="button"
                    className="survey-element-table__pick"
                    aria-pressed={selectedElementId === element.id}
                    onClick={() =>
                      onSelectElement(
                        selectedElementId === element.id ? undefined : element.id,
                      )
                    }
                  >
                    {element.label}
                  </button>
                  {edited?.get(element.id) ? (
                    <span
                      className="survey-element-table__edited"
                      title={
                        edited.get(element.id) === "followed"
                          ? "Moved with a wall corrected on the floor plan: this figure follows the designer's walls, not the scanner's"
                          : "Corrected on the floor plan: this figure is the designer's, not the scanner's"
                      }
                    >
                      {edited.get(element.id) === "added"
                        ? " added on plan"
                        : edited.get(element.id) === "followed"
                          ? " moved with an edited wall"
                          : " edited on plan"}
                    </span>
                  ) : null}
                </th>
                {renderRow(element)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * A scanned wall's "Other side" in the Walls table: the Rooms table's own
 * cell (`WallAdjacencyCheck`) for the heat-loss wall it became — the
 * "Outside wall? — may be a party wall" marker and its three answers, or what
 * was answered. Not the anchor copy; the Rooms table's is. A wall the heat
 * loss does not have (added on the plan since, say) shows a dash.
 */
function OtherSideCell({
  wallId,
  otherSide,
}: {
  readonly wallId: string;
  readonly otherSide: NonNullable<Parameters<typeof RoomDetail>[0]["otherSide"]>;
}): ReactNode {
  const found = otherSide.find(wallId);
  return (
    <td className="survey-element-table__other-side">
      {found ? (
        <WallAdjacencyCheck
          surface={found.surface}
          roomName={found.room.name}
          canAnswer={otherSide.canAnswer}
          onAnswered={otherSide.onAnswered}
          anchor={false}
        />
      ) : (
        <span className="muted" title="This wall is not in the heat loss.">
          —
        </span>
      )}
    </td>
  );
}

function kindLabel(element: ScanElement): string {
  switch (element.kind) {
    case "window":
      return "Window";
    case "door":
      return "Door";
    case "opening":
      return "Opening";
    default:
      return element.kind;
  }
}


/**
 * A placeholder for the design tools that will hang off the survey — annotate
 * walls, drop heat-pump placements, radiators. The button is disabled on
 * purpose: the tools do not exist yet, and a menu that opens onto nothing
 * would read as broken. The tooltip says what will live behind it.
 */
function DesignToolbar(): ReactNode {
  return (
    <div className="survey-design-toolbar" role="group" aria-label="Design tools">
      <button
        type="button"
        disabled
        className="survey-design-toolbar__btn"
        title="Design tools coming — you'll be able to annotate walls, add heat-pump placements and radiators from here."
      >
        Edit design ▾
      </button>
    </div>
  );
}

/**
 * The textured "3D Space" model, and honestly, its state.
 *
 * The flat scan model above is built from the RoomPlan geometry the moment a
 * capture syncs. This textured model is built by a separate pipeline — Apple's
 * PhotogrammetrySession on the build Mac, a minute or two for a room — from
 * the phone's Space Mode capture. So a good part of what this section shows is
 * a status: queued, processing (with the solver's own progress and stage),
 * failed (with the reason), or nothing captured at all. It never shows a
 * placeholder render in place of the real one; it shows a model only once the
 * API reports one ready with a file to fetch.
 *
 * While the model is queued or processing the status is re-read every few
 * seconds (`useSpaceModel`), so a reader who opened the survey to watch it
 * sees the model arrive without pressing anything.
 *
 * `client.spaceModel` turns "nothing captured" — a `{ status: null }` answer,
 * or a 404/501 from an API without the route — into `null`, which reads as the
 * honest empty state rather than an error.
 */
function SpaceModelSection({ surveyId }: { readonly surveyId: string }): ReactNode {
  const { client } = usePortal();
  const { state, stale, refresh } = useSpaceModel(() => client.spaceModel(surveyId), surveyId);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = async (): Promise<void> => {
    setDownloadError(null);
    try {
      const file = await client.spaceModelUsdz(surveyId);
      const a = document.createElement("a");
      a.href = file.url;
      a.download = file.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Long enough for the browser to take the bytes; then let the blob go.
      setTimeout(() => URL.revokeObjectURL(file.url), 60_000);
    } catch {
      setDownloadError("The model could not be downloaded. Try again.");
    }
  };

  return (
    <section id="space" aria-labelledby="space-heading">
      <h2 id="space-heading">3D Space</h2>
      <Async state={state} what="the 3D space model" reload={refresh}>
        {(model) => (
          <SpaceModelBody
            model={model}
            stale={stale}
            refresh={refresh}
            loadModel={() => client.spaceModelGlb(surveyId)}
            onDownload={download}
            downloadError={downloadError}
          />
        )}
      </Async>
    </section>
  );
}

function SpaceModelBody({
  model,
  stale,
  refresh,
  loadModel,
  onDownload,
  downloadError,
}: {
  readonly model: SpaceModel | null;
  readonly stale: boolean;
  readonly refresh: () => void;
  readonly loadModel: () => Promise<ArrayBuffer>;
  readonly onDownload: () => Promise<void>;
  readonly downloadError: string | null;
}): ReactNode {
  if (model === null) {
    return (
      <div className="state state--empty space-status">
        <p>
          <strong>No textured 3D space model has been captured for this survey yet.</strong>
        </p>
        <p className="small muted">
          On the phone: New survey → 3D space → Space Mode → Capture a space. Walk
          slowly round the room, tap Done, then Send the survey from Projects. The
          model is built on the server and appears here a few minutes later.
        </p>
      </div>
    );
  }

  const staleNote = stale ? (
    <p className="small muted" role="note">
      The last check did not reach the API; this is the status from before. Still trying.
    </p>
  ) : null;
  const received =
    typeof model.bundleBytes === "number" ? ` Capture received: ${formatMegabytes(model.bundleBytes)}.` : "";

  switch (model.status) {
    case "queued":
      return (
        <div className="notice pending space-status" role="status">
          <p>
            <strong>Queued.</strong> The capture has arrived and is waiting its turn to be
            built — models are built one at a time.{received}
          </p>
          {typeof model.secondsSinceQueued === "number" ? (
            <p className="small muted">
              Waiting {formatDuration(model.secondsSinceQueued)}. This updates on its own.
            </p>
          ) : null}
          {staleNote}
          <button type="button" onClick={refresh}>Check again</button>
        </div>
      );

    case "processing": {
      const fraction = typeof model.progress === "number" ? Math.min(1, Math.max(0, model.progress)) : null;
      const percent = fraction === null ? null : Math.round(fraction * 100);
      const stage = spaceStageLabel(model.stage);
      return (
        <div className="notice pending space-status" role="status">
          <p>
            <strong>Processing.</strong> The textured model is being built from the
            capture. A room usually takes one to three minutes.{received}
          </p>
          <div
            className="space-progress"
            role="progressbar"
            aria-label="Building the 3D model"
            aria-valuemin={0}
            aria-valuemax={100}
            {...(percent === null ? {} : { "aria-valuenow": percent })}
          >
            <div
              className={"space-progress__bar" + (percent === null ? " is-indeterminate" : "")}
              style={percent === null ? undefined : { width: `${percent}%` }}
            />
          </div>
          <p className="small muted">
            {[
              percent === null ? null : `${percent}%`,
              stage,
              typeof model.secondsProcessing === "number"
                ? `processing for ${formatDuration(model.secondsProcessing)}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            . This updates on its own.
          </p>
          {staleNote}
          <button type="button" onClick={refresh}>Check again</button>
        </div>
      );
    }

    case "failed":
      return (
        <div className="notice fail space-status" role="alert">
          <p>
            <strong>The textured model could not be built.</strong> The flat scan
            model and every measurement above are unaffected — those come from the
            capture directly, not from this pipeline.
          </p>
          {model.error ? <p className="small">Reason: {model.error}</p> : null}
          <p className="small muted">
            To try again, capture the room again in Space Mode on the phone and send
            the survey; the new capture replaces this one.
          </p>
          <button type="button" onClick={refresh}>Check again</button>
        </div>
      );

    case "ready":
      if (!model.modelUrl) {
        if (model.usdzUrl) {
          // A good reconstruction whose web rendition failed: the model
          // exists, only this browser view of it does not.
          return (
            <div className="notice warn space-status" role="note">
              <p>
                <strong>The model is built, but its web view could not be made.</strong>{" "}
                Download it to open it in Quick Look on an iPhone, iPad or Mac.
              </p>
              <button type="button" onClick={() => void onDownload()}>Download model (USDZ)</button>
              {downloadError ? <p className="small" role="alert">{downloadError}</p> : null}
            </div>
          );
        }
        // The API said ready but gave nothing to load. Say so rather than mount
        // a viewer on an empty URL and show a blank canvas.
        return (
          <div className="notice fail" role="alert">
            <p>
              The model is marked ready but the API returned no file to load.
            </p>
          </div>
        );
      }
      return (
        <>
          <p className="muted small">
            A textured mesh built from the survey&rsquo;s Space Mode photographs. Drag to
            turn, scroll to zoom. It shows what the room looks like — the measurements
            a design depends on are in the sections above.
          </p>
          {downloadError ? <p className="small" role="alert">{downloadError}</p> : null}
          <div className="space-model-frame">
            <SpaceWorkspace
              // A re-capture replaces the model behind the same URL; the
              // finish time is what tells the viewer to reload.
              modelUrl={`${model.modelUrl}#${model.finishedAt ?? ""}`}
              loadModel={loadModel}
              {...(model.usdzUrl ? { onDownload } : {})}
              info={{
                imageCount: model.imageCount ?? null,
                metricScale: model.metricScale ?? null,
                secondsProcessing: model.secondsProcessing ?? null,
                finishedAt: model.finishedAt ?? null,
              }}
            />
          </div>
        </>
      );
  }
}

/**
 * Marks the survey finished. After this the API holds it read-only.
 *
 * Shown only where `survey.status` is still `draft`, and only to a role that
 * holds `survey: update` — asked of the same matrix the API enforces rather
 * than guessed from the role name, so office, who may read a survey but never
 * change one, sees no button that could only come back refused.
 *
 * `isAssigned` is passed as `true` unconditionally: this screen only ever
 * renders for a survey this browser has already read, and a role holding
 * `survey` at "own" scope could not have reached that read unless it were
 * assigned to the job — so the fact the server would check is already
 * established by the time this button is on screen.
 */
function MarkComplete({
  survey,
  me,
  onCompleted,
}: {
  readonly survey: SurveySummary;
  readonly me: Me;
  readonly onCompleted: () => void;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();

  if (survey.status !== "draft") return null;

  const mayComplete = can({
    role: me.role,
    resource: "survey",
    action: "update",
    isAssigned: true,
  });
  if (!mayComplete) return null;

  return (
    <div className="notice pending">
      <p>
        <strong>Still open.</strong> Marking a survey complete says the visit
        is done. It can still be corrected afterwards — only a survey replaced
        by a newer one is locked.
      </p>
      <button
        disabled={busy}
        onClick={() =>
          void submit(async () => {
            await client.completeSurvey(survey.id);
            onCompleted();
          })
        }
      >
        {busy ? "Marking complete…" : "Mark this survey complete"}
      </button>
      {error !== null ? (
        <Failure error={error} what="marking this survey complete" />
      ) : null}
    </div>
  );
}

/**
 * The four questions a designer asks before anything else: what shape of
 * building, what kind of dwelling, what the walls are made of, and whether
 * they are insulated.
 *
 * NOT RECORDED IS A STATE, NOT A GAP. Each select opens on "Not recorded" when
 * nobody has answered, and choosing it again clears an answer. Nothing is
 * pre-selected: a default here would print the portal's guess as though a
 * surveyor had said it, which is the one thing this screen exists to prevent.
 *
 * Editable only while the survey is a draft, and only for a role holding
 * `survey: update` — asked of the same matrix the API enforces, as
 * `MarkComplete` does. Everyone else sees the answers as plain values.
 *
 * Saving sends only the fields that changed, and the card then shows what the
 * API says is stored, not what was sent.
 *
 * These facts do not feed the heat loss yet; the line under the heading says
 * so, so nobody reads a changed wall type as a changed U-value.
 */
function PropertyCard({
  survey,
  property,
  me,
}: {
  readonly survey: SurveySummary;
  readonly property: SurveyPropertyDetails;
  readonly me: Me;
}): ReactNode {
  const { client } = usePortal();
  const { busy, error, submit } = useSubmit();
  const [saved, setSaved] = useState<SurveyPropertyDetails>(property);
  const [draft, setDraft] = useState<SurveyPropertyFacts>(() => factsOf(property));
  const [justSaved, setJustSaved] = useState(false);

  const editable =
    survey.status !== "superseded" &&
    can({ role: me.role, resource: "survey", action: "update", isAssigned: true });

  const changed = SURVEY_PROPERTY_FIELDS.filter((field) => draft[field] !== saved[field]);

  function choose(field: keyof SurveyPropertyFacts, raw: string): void {
    setJustSaved(false);
    setDraft((current) => ({ ...current, [field]: raw === "" ? null : raw }));
  }

  function save(): void {
    void submit(async () => {
      const input: SurveyPropertyInput = {};
      for (const field of changed) {
        (input as Record<string, unknown>)[field] = draft[field];
      }
      const stored = await client.updateSurveyProperty(survey.id, input);
      setSaved(stored);
      setDraft(factsOf(stored));
      setJustSaved(true);
    });
  }

  return (
    <section aria-labelledby="survey-property-heading">
      <h2 id="survey-property-heading">Property</h2>
      <p className="muted small survey-property__lede">
        Recorded by the surveyor. Not yet used by the heat loss — U-values are
        still set per surface below.
      </p>

      {editable ? (
        <form
          className="survey-property__form"
          onSubmit={(event) => {
            event.preventDefault();
            save();
          }}
        >
          <div className="survey-property__grid">
            {PROPERTY_QUESTIONS.map((question) => (
              <div className="field" key={question.field}>
                <label htmlFor={`survey-property-${question.field}`}>
                  {question.label}
                </label>
                <select
                  id={`survey-property-${question.field}`}
                  value={draft[question.field] ?? ""}
                  disabled={busy}
                  onChange={(event) => choose(question.field, event.target.value)}
                >
                  <option value="">Not recorded</option>
                  {question.options.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <div className="survey-property__actions">
            <button type="submit" disabled={busy || changed.length === 0}>
              {busy ? "Saving…" : "Save property details"}
            </button>
            <span className="small muted" role="status">
              {justSaved && changed.length === 0
                ? "Saved."
                : changed.length > 0
                  ? `${changed.length} unsaved ${changed.length === 1 ? "change" : "changes"}`
                  : ""}
            </span>
          </div>
          {error !== null ? (
            <Failure error={error} what="saving the property details" />
          ) : null}
        </form>
      ) : (
        <dl className="fields">
          {PROPERTY_QUESTIONS.map((question) => (
            <div className="row" key={question.field}>
              <dt>{question.label}</dt>
              <dd>
                {labelFor(question.options, saved[question.field]) ?? (
                  <span className="muted">Not recorded</span>
                )}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <p className="hint">
        {saved.updatedAt === null
          ? "Nothing recorded yet."
          : `Last changed ${utcMoment(saved.updatedAt)}` +
            (saved.updatedBy !== null && saved.updatedBy.name !== ""
              ? ` by ${saved.updatedBy.name}`
              : "") +
            "."}
        {survey.status === "superseded"
          ? ` Read-only now the survey is ${humanise(survey.status).toLowerCase()}.`
          : ""}
      </p>
    </section>
  );
}

interface PropertyQuestion {
  readonly field: keyof SurveyPropertyFacts;
  readonly label: string;
  readonly options: readonly VocabularyOption<string>[];
}

const PROPERTY_QUESTIONS: readonly PropertyQuestion[] = [
  { field: "propertyType", label: "Property type", options: PROPERTY_TYPE_OPTIONS },
  { field: "builtForm", label: "Built form", options: BUILT_FORM_OPTIONS },
  { field: "wallConstruction", label: "Wall type", options: WALL_CONSTRUCTION_OPTIONS },
  { field: "wallInsulation", label: "Wall insulation", options: WALL_INSULATION_OPTIONS },
];

function factsOf(details: SurveyPropertyDetails): SurveyPropertyFacts {
  return {
    builtForm: details.builtForm,
    propertyType: details.propertyType,
    wallConstruction: details.wallConstruction,
    wallInsulation: details.wallInsulation,
  };
}

function labelFor(
  options: readonly VocabularyOption<string>[],
  value: string | null,
): string | null {
  if (value === null) return null;
  // A key this build does not know (a newer API) is shown as itself rather
  // than hidden — the survey does hold an answer.
  return options.find((option) => option.value === value)?.label ?? humanise(value);
}

/** Every provenanced value in the survey, for the tally at the top. */
function collectValues(rooms: readonly SurveyRoom[]): ProvenancedValue[] {
  const out: ProvenancedValue[] = [];
  for (const room of rooms) {
    out.push(room.floorAreaM2, room.volumeM3, room.airChangesPerHour);
    for (const surface of room.surfaces) {
      out.push(surface.areaM2, surface.uValue);
    }
  }
  return out;
}

/**
 * The heat loss, asked for in compliance mode.
 *
 * `compliance: true` makes the API refuse rather than warn, and the refusal
 * carries every rule breached. That is the answer worth showing: a design that
 * cannot be issued should say so here, not at the point somebody tries to
 * produce the document.
 */
function HeatLoss({
  surveyId,
  planEdits,
  revision = 0,
  assumedOutsideWalls = 0,
  validation,
}: {
  readonly surveyId: string;
  readonly planEdits?: PlanEditsState;
  /** Bumped when the rooms were re-converted (a laser calibration, a plan correction): re-read. */
  readonly revision?: number;
  /** Walls counted as outside only because nothing was scanned behind them. */
  readonly assumedOutsideWalls?: number;
  /**
   * The same report the Checks list beside the plan shows. While it has a
   * blocking problem in it, this section says plainly that the figure cannot
   * be trusted yet — the one thing a number this size must never do is look
   * finished before it is.
   */
  readonly validation?: ValidationReport;
}): ReactNode {
  const stale = planEdits ? heatLossPlanNotice(planEdits) : null;
  const trust = validation ? heatLossTrustSentence(validation) : null;
  const { client } = usePortal();
  const { state, reload } = useAsync<SurveyHeatLoss>(
    () => client.heatLoss(surveyId, { compliance: true }),
    [client, surveyId, revision],
  );

  return (
    <section>
      <h2>Heat loss</h2>
      {/* First, and above everything else in this section: a figure that
          cannot be trusted yet must say so before it is read, not after. */}
      {trust !== null ? (
        <div className="notice fail" role="note" data-testid="heat-loss-not-trusted">
          <p className="small">
            <strong>Not a final figure.</strong> {trust}
          </p>
        </div>
      ) : null}
      {/* Never silently: a saved plan correction re-converts the rooms and
          these figures are re-read; where it could not (a completed survey,
          a server without the scan), this says the plan has moved on. */}
      {stale ? (
        <div className="notice warn" role="note" data-testid="heat-loss-plan-edited">
          <p className="small">
            <strong>Plan edited since the heat loss was calculated.</strong> {stale}
          </p>
        </div>
      ) : null}
      {/* Never silently too high: a party wall counted as outside overstates
          the load, so the figure says it rests on those walls until each is
          answered in the Rooms tables below. */}
      {assumedOutsideWalls > 0 ? (
        <div className="notice warn" role="note" data-testid="heat-loss-outside-walls">
          <p className="small">
            <strong>
              Counts {assumedOutsideWalls} {assumedOutsideWalls === 1 ? "wall" : "walls"} as outside
              that may be party walls.
            </strong>{" "}
            {/* A button, not a #fragment link: the portal's router owns the hash. */}
            <button
              type="button"
              className="link"
              onClick={() =>
                document.getElementById("survey-rooms-heading")?.scrollIntoView({ block: "start" })
              }
            >
              Check them under Rooms
            </button>
            .
          </p>
        </div>
      ) : null}
      <Async state={state} what="the heat loss" reload={reload}>
        {(result) => {
          const h = result.heatLoss;
          const reviewNotes = result.reviewNotes ?? [];
          return (
            <>
              {/* A laser reading or a plan correction brings the scan's own
                  figures up to date but never overwrites a person's; where a
                  kept value and the new geometry now disagree (or a room or
                  wall with a person's work is off the plan), the figure rests
                  on it, so it says so — while it is still true. */}
              {reviewNotes.length > 0 ? (
                <div className="notice warn" role="note" data-testid="heat-loss-review-notes">
                  <p className="small">
                    <strong>
                      Kept as a person entered {reviewNotes.length === 1 ? "it" : "them"} — check{" "}
                      {reviewNotes.length === 1 ? "this" : "these"} against the plan.
                    </strong>
                  </p>
                  <ul className="small">
                    {reviewNotes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <dl className="fields">
                <div className="row">
                  <dt>Design heat load</dt>
                  <dd>
                    <strong>{watts(h.totalHeatLossW)}</strong>{" "}
                    <span className="muted small">
                      the heat pump must meet at least 100% of this
                    </span>
                  </dd>
                </div>
                <div className="row">
                  <dt>Transmission</dt>
                  <dd>{watts(h.totalTransmissionW)}</dd>
                </div>
                <div className="row">
                  <dt>Thermal bridging</dt>
                  <dd>{watts(h.totalThermalBridgingW)}</dd>
                </div>
                <div className="row">
                  <dt>Ventilation</dt>
                  <dd>{watts(h.totalVentilationW)}</dd>
                </div>
                <div className="row">
                  <dt>Specific heat loss</dt>
                  <dd>
                    {wattsPerM2(h.wattsPerM2)}{" "}
                    <span className="muted small">
                      over {squareMetres(h.totalFloorAreaM2)}
                    </span>
                  </dd>
                </div>
              </dl>

              {h.warnings.length > 0 ? (
                <div className="notice warn">
                  <p className="small">
                    <strong>What the engine flagged</strong>
                  </p>
                  <ul className="small">
                    {h.warnings.map((warning) => (
                      <li key={`${warning.code}:${warning.subjectId ?? ""}`}>
                        {warning.message}{" "}
                        <span className="mono small">{warning.code}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              <div className="table-scroll">
                <table>
                  <caption>Room by room, as MIS 3005-D §3.8.1(a) requires</caption>
                  <thead>
                    <tr>
                      <th scope="col">Room</th>
                      <th scope="col">Design</th>
                      <th scope="col">Transmission</th>
                      <th scope="col">Ventilation</th>
                      <th scope="col">Total</th>
                      <th scope="col">W/m²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {h.rooms.map((room) => (
                      <tr key={room.roomId}>
                        <th scope="row">{room.name}</th>
                        <td>{degrees(room.designTempC)}</td>
                        <td>{watts(room.transmissionW)}</td>
                        <td>{watts(room.ventilationW)}</td>
                        <td>
                          <strong>{watts(room.totalW)}</strong>
                        </td>
                        <td>{wattsPerM2(room.wattsPerM2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          );
        }}
      </Async>
    </section>
  );
}

/**
 * The U-values nobody has confirmed, worst first.
 *
 * This is the work list. Confirming one is the cheapest accuracy improvement
 * available anywhere in the product, which is why it is a button here rather
 * than a field on a form somewhere else.
 */
function Assumptions({
  surveyId,
  onConfirmed,
  revision = 0,
  canConfirm = true,
}: {
  readonly surveyId: string;
  readonly onConfirmed: () => void;
  /** Bumped when the rooms were re-converted: their surfaces are new rows, so re-read. */
  readonly revision?: number;
  /**
   * May confirm a U-value — `survey: update`, as the API enforces it. Without
   * it the list is shown and the Confirm column is not rendered at all.
   */
  readonly canConfirm?: boolean;
}): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<Assumption[]>(
    () => client.assumptions(surveyId),
    [client, surveyId, revision],
  );

  return (
    <section className="has-assumptions">
      <h2>Unconfirmed assumptions</h2>
      <Async state={state} what="the assumptions" reload={reload}>
        {(assumptions) =>
          assumptions.length === 0 ? (
            <p className="empty">
              Every U-value in this survey has been confirmed by a surveyor.
            </p>
          ) : (
            <>
              <p className="muted small">
                {assumptions.length} unconfirmed, worst U-value first. These
                dominate the error in the heat loss above.
              </p>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th scope="col">Surface</th>
                      <th scope="col">Assumed U-value</th>
                      <th scope="col">Source</th>
                      {canConfirm ? <th scope="col">Confirm</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {assumptions.map((assumption) => (
                      <AssumptionRow
                        key={assumption.surfaceId}
                        assumption={assumption}
                        canConfirm={canConfirm}
                        onDone={() => {
                          reload();
                          onConfirmed();
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        }
      </Async>
    </section>
  );
}

function AssumptionRow({
  assumption,
  onDone,
  canConfirm,
}: {
  readonly assumption: Assumption;
  readonly onDone: () => void;
  readonly canConfirm: boolean;
}): ReactNode {
  const { client } = usePortal();
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function confirm(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      // An empty box is not a no-op: it records that somebody looked at the
      // assumption and accepted it, which is a stronger statement than nobody
      // having looked. The API models that distinction, so the UI must too.
      const trimmed = value.trim();
      await client.confirmUValue(
        assumption.surfaceId,
        trimmed === "" ? {} : { uValue: Number(trimmed) },
      );
      onDone();
    } catch (cause: unknown) {
      setError(cause);
      setBusy(false);
    }
  }

  const parsed = value.trim() === "" ? null : Number(value);
  const invalid = parsed !== null && (Number.isNaN(parsed) || parsed <= 0);

  return (
    <tr className="assumed-cell">
      <th scope="row">
        {assumption.roomName}
        <span className="muted"> · </span>
        {assumption.surfaceName}
        {assumption.construction ? (
          <>
            <br />
            <span className="muted small">{assumption.construction}</span>
          </>
        ) : null}
      </th>
      <td>
        <span className="n">{formatU(assumption.uValue)}</span>
      </td>
      <td>
        <ProvenanceChip
          value={{
            value: assumption.uValue,
            provenance: assumption.provenance,
            confidence: null,
            confirmedBySurveyor: false,
          }}
        />
      </td>
      {canConfirm ? (
      <td>
        <label
          className="visually-hidden"
          htmlFor={`u-${assumption.surfaceId}`}
        >
          Measured U-value for {assumption.surfaceName} in {assumption.roomName}
        </label>
        <input
          id={`u-${assumption.surfaceId}`}
          inputMode="decimal"
          placeholder="Accept as is"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />{" "}
        <button disabled={busy || invalid} onClick={() => void confirm()}>
          {busy ? "Saving…" : value.trim() === "" ? "Accept" : "Confirm"}
        </button>
        {invalid ? (
          <p className="hint small fail">A U-value has to be a number above zero.</p>
        ) : null}
        {error !== null ? <Failure error={error} what="that confirmation" /> : null}
      </td>
      ) : null}
    </tr>
  );
}

/**
 * Where a converted room's scanned figures came from when a laser reading
 * calibrated them — "Areas and volume from the LiDAR scan, calibrated ×1.0071
 * from a laser reading (J. Ryder, 11 Sep)." — in the scan workspace's own
 * words (`calibrationMarker`). Nothing at all for an uncalibrated room:
 * calibration is optional, and its chips already say "Scanned". Shown only
 * while the figures are still the scan's; a value a person typed over says so
 * in its own chip.
 */
function ScanCalibrationNote({ room }: { readonly room: SurveyRoom }): ReactNode {
  const cal = room.scanCalibration;
  if (!cal) return null;
  if (room.floorAreaM2.provenance !== "scanned" && room.volumeM3.provenance !== "scanned") return null;
  const marker = calibrationMarker({
    factor: cal.factor,
    source: cal.scope,
    calibration: {
      id: cal.calibrationId,
      scope: { kind: "scan" },
      factor: cal.factor,
      ...(cal.at !== null ? { provenance: { byName: cal.byName, at: cal.at } } : {}),
    },
  });
  return (
    <p className="muted small" data-testid="room-scan-calibration">
      Areas and volume from the LiDAR scan, {marker}.
    </p>
  );
}

/**
 * What the built form suggests about the walls the scan called outside: how
 * many party walls to expect, as a suggestion. Nothing here changes a wall —
 * each one is answered on its own row, by a person.
 */
function OutsideWallsNotice({
  count,
  builtForm,
  partyWalls,
}: {
  readonly count: number;
  readonly builtForm: BuiltForm | null;
  /** Wall surfaces already set to party. */
  readonly partyWalls: number;
}): ReactNode {
  if (count === 0) return null;
  return (
    <div className="notice warn adjacency-notice" role="note" data-testid="outside-walls-notice">
      <p className="small">
        <strong>
          {count} {count === 1 ? "wall was" : "walls were"} counted as outside because nothing was
          scanned behind {count === 1 ? "it" : "them"}.
        </strong>{" "}
        On a terrace or a semi some will be party walls, which lose far less heat. Each is marked{" "}
        <em>Outside wall?</em> below, and in the Walls table under the 3D view, beside the wall itself.
      </p>
      <p className="small" data-testid="built-form-suggestion">
        {builtFormSuggestion(builtForm)}
        {partyWalls > 0 ? (
          <span className="muted">
            {" "}
            {partyWalls} {partyWalls === 1 ? "wall is" : "walls are"} set as party walls so far.
          </span>
        ) : null}
      </p>
    </div>
  );
}

function Room({
  room,
  canAnswer = false,
  onAnswered = () => {},
}: {
  readonly room: SurveyRoom;
  /** May say what is on the other side of a wall (`survey: update`, a draft survey). */
  readonly canAnswer?: boolean;
  /** A wall's adjacency was answered: re-read the survey and the heat loss. */
  readonly onAnswered?: () => void;
}): ReactNode {
  // Walls only: the API marks an assumed floor with the same flag, and this
  // count is followed by the word "walls". The floor's question is asked in
  // the floor's own panel and counted in the Checks list beside the plan.
  const toCheck = room.surfaces.filter(
    (s) => s.kind === "wall" && s.adjacencyAssumed === true,
  ).length;
  // A room with a wall to check starts open, so the question is on screen
  // rather than behind a disclosure; after that it is the reader's to fold.
  const [open, setOpen] = useState(toCheck > 0);
  return (
    <details className="room" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>
        {room.name} <span className="muted small">{humanise(room.type)}</span>
        {toCheck > 0 ? (
          <span className="adjacency-count" data-testid="room-walls-to-check">
            {" "}
            {toCheck} {toCheck === 1 ? "wall" : "walls"} to check
          </span>
        ) : null}
      </summary>

      <dl className="fields">
        <div className="row">
          <dt>Design temperature</dt>
          <dd>
            {room.designTempC === null ? (
              <span className="muted">
                MIS 3005-D Table 1 minimum for a {humanise(room.type)}
              </span>
            ) : (
              degrees(room.designTempC)
            )}
          </dd>
        </div>
        <div className="row">
          <dt>Floor area</dt>
          <dd>
            <ProvenancedNumber value={room.floorAreaM2} format={squareMetres} />
          </dd>
        </div>
        <div className="row">
          <dt>Volume</dt>
          <dd>
            <ProvenancedNumber value={room.volumeM3} format={cubicMetres} />
          </dd>
        </div>
        <div className="row">
          <dt>Air changes</dt>
          <dd>
            <ProvenancedNumber value={room.airChangesPerHour} format={airChanges} />
          </dd>
        </div>
      </dl>

      <ScanCalibrationNote room={room} />

      <div className="table-scroll">
        <table>
          <caption className="visually-hidden">
            Surfaces in {room.name}
          </caption>
          <thead>
            <tr>
              <th scope="col">Surface</th>
              <th scope="col">Area</th>
              <th scope="col">U-value</th>
              <th scope="col">Other side</th>
            </tr>
          </thead>
          <tbody>
            {room.surfaces.map((surface) => (
              <tr key={surface.id}>
                <th scope="row">
                  {surface.name ?? humanise(surface.kind)}
                  <br />
                  <span className="muted small">{humanise(surface.kind)}</span>
                </th>
                <td>
                  <ProvenancedNumber value={surface.areaM2} format={squareMetres} />
                </td>
                <td>
                  <ProvenancedNumber value={surface.uValue} format={formatU} />
                </td>
                <td>
                  <WallAdjacencyCheck
                    surface={surface}
                    roomName={room.name}
                    canAnswer={canAnswer}
                    onAnswered={onAnswered}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}
